import React, { useEffect, useState, useRef } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    getEpisodes,
    getEpisode,
    updateEpisode,
    createEpisode,
    getPodcasts,
    uploadEpisodeMedia,
    uploadEpisodeArtwork,
    deleteEpisode,
    notifyEpisode,
    getEpisodeChangeLogs,
    getCurrentUser,
    listEpisodeFiles,
    uploadEpisodeFile,
    completeEpisodeFileUploadBatch,
    useEpisodeFileAsAudio,
    useEpisodeFileAsArtwork,
    transcribeEpisodeAudio,
    processEpisodeAudio,
    processEpisodeMultitrack,
    generateEpisodeShowNotes,
    getAuphonicPresets,
    getShowNotesSettings,
    getEpisodeFileStreamUrl,
    buildEpisodeFileStreamProxyUrl,
    createEpisodeFolder,
    deleteEpisodeFileItem,
    renameEpisodeFileItem,
    listEpisodeExternalLinks,
    createEpisodeExternalLink,
    updateEpisodeExternalLink,
    deleteEpisodeExternalLink,
    createEpisodeSharePointFolder,
    createEpisodeShare,
    sendEpisodeShareEmails,
} from "./api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { diffWords } from "diff";
import { sanitizeHtml } from "./sanitizeHtml";
import AudioPlayer from "./AudioPlayer";

/* --------------------------------------------
   STATUS COLOUR PILL (for cards)
-------------------------------------------- */
function getStatusColor(status) {
    const s = (status || "").toLowerCase();

    switch (s) {
        case "planned":
            return { bg: "#e0f2fe", text: "#0369a1" };
        case "requested":
            return { bg: "#ede9fe", text: "#6d28d9" };
        case "pending approval":
            return { bg: "#fef9c3", text: "#a16207" };
        case "scheduled":
            return { bg: "#dcfce7", text: "#166534" };
        case "approved":
            return { bg: "#d9f99d", text: "#4d7c0f" };
        case "published":
            return { bg: "#bbf7d0", text: "#15803d" };
        case "rejected":
            return { bg: "#fee2e2", text: "#b91c1c" };
        default:
            return { bg: "#e5e7eb", text: "#374151" };
    }
}

/* --------------------------------------------
   POWER APPS STYLE "URGENCY" COLOUR
   (used as left accent bar on cards)
-------------------------------------------- */
function getEpisodeRowColor(ep) {
    const GREEN = "#16a34a";
    const RED = "#dc2626";
    const YELLOW = "#eab308";
    const PURPLE = "#6d28d9";
    const NEUTRAL = "#1f2937";

    const status = (ep.status || "").toLowerCase().trim();

    let pub = null;
    if (ep.publication_date) {
        const d = new Date(ep.publication_date);
        if (!isNaN(d.getTime())) {
            pub = new Date(d);
            pub.setHours(0, 0, 0, 0);
        }
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (!pub) {
        return PURPLE;
    }

    if (["scheduled", "approved", "published"].includes(status)) {
        return GREEN;
    }

    if (status === "planned" && pub && pub <= tomorrow) {
        return RED;
    }

    if (status === "pending approval" && pub && pub < today) {
        return RED;
    }

    if (status === "pending approval") {
        return YELLOW;
    }

    if (pub && pub.getTime() === today.getTime()) {
        return YELLOW;
    }

    if (pub && pub < today) {
        return RED;
    }

    return NEUTRAL;
}

/* --------------------------------------------
   FORMAT DATETIME for client_reviewed_at / logs
-------------------------------------------- */
function formatDateTime(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function toLocalYmd(dateObj) {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function formatPublishDateLabel(value) {
    if (!value) return "";
    const text = String(value).slice(0, 10);
    const dt = new Date(`${text}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return String(value);

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (text === toLocalYmd(today)) return "Today";
    if (text === toLocalYmd(tomorrow)) return "Tomorrow";
    return dt.toLocaleDateString("en-GB");
}

function isDistributionEnabled(value) {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value).trim().toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    return true;
}

function formatEpisodeCodeLabel(seasonNumber, episodeNumber) {
    const episode = Number.parseInt(episodeNumber, 10);
    if (!Number.isFinite(episode) || episode <= 0) return "";

    const season = Number.parseInt(seasonNumber, 10);
    if (Number.isFinite(season) && season > 0) {
        return `S${season}E${episode}`;
    }

    return `E${episode}`;
}

/* --------------------------------------------
   BEST-EFFORT FIX FOR MIS-ENCODED FILENAMES
-------------------------------------------- */
function decodeFilename(name) {
    if (!name) return name;
    try {
        return decodeURIComponent(escape(name));
    } catch {
        return name;
    }
}

function formatFileSize(bytes) {
    const value = Number(bytes || 0);
    if (!value) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
        size /= 1024;
        idx += 1;
    }
    return `${size.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatModified(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getFileExtension(name) {
    const raw = String(name || "").trim();
    const idx = raw.lastIndexOf(".");
    if (idx <= 0 || idx === raw.length - 1) return "";
    return raw.slice(idx + 1).toLowerCase();
}

function splitFileNameAndExtension(name) {
    const raw = String(name || "").trim();
    const idx = raw.lastIndexOf(".");
    if (idx <= 0 || idx === raw.length - 1) {
        return { baseName: raw, extensionWithDot: "" };
    }
    return {
        baseName: raw.slice(0, idx),
        extensionWithDot: raw.slice(idx),
    };
}

function getPathLeafName(pathValue) {
    const parts = String(pathValue || "")
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean);
    return parts.length ? parts[parts.length - 1] : "";
}

function isAudioFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("audio/")) return true;
    const ext = getFileExtension(item?.name);
    return [
        "mp3",
        "m4a",
        "aac",
        "wav",
        "flac",
        "ogg",
        "oga",
        "opus",
        "mpga",
        "aif",
        "aiff",
        "wma",
    ].includes(ext);
}

function isImageFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("image/")) return true;
    const ext = getFileExtension(item?.name);
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic"].includes(ext);
}

function isVideoFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("video/")) return true;
    const ext = getFileExtension(item?.name);
    return ["mp4"].includes(ext);
}

function isBlobUrl(value) {
    return String(value || "").startsWith("blob:");
}

function joinPath(basePath, segment) {
    if (!basePath || basePath === "/") return `/${segment}`;
    return `${basePath}/${segment}`.replace(/\/{2,}/g, "/");
}

function createMultitrackRow() {
    return {
        row_id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        item_id: "",
        track_name: "",
    };
}

function normalizeExternalLinkUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const withScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw)
        ? raw
        : raw.startsWith("//")
          ? `https:${raw}`
          : `https://${raw}`;
    try {
        const parsed = new URL(withScheme);
        const protocol = String(parsed.protocol || "").toLowerCase();
        if (protocol !== "http:" && protocol !== "https:") {
            return "";
        }
        return parsed.toString();
    } catch {
        return "";
    }
}

function breadcrumbSegments(path) {
    const safe = String(path || "/");
    if (safe === "/") return [{ label: "Home", path: "/" }];

    const parts = safe.split("/").filter(Boolean);
    const out = [{ label: "Home", path: "/" }];
    let current = "";
    for (const part of parts) {
        current += `/${part}`;
        out.push({ label: part, path: current });
    }
    return out;
}

function isExternalFileDrag(e) {
    const types = Array.from(e?.dataTransfer?.types || []);
    return types.includes("Files");
}

/* --------------------------------------------
   CHANGE LOG DIFF SUMMARY (generic)
-------------------------------------------- */
function summariseDiff(rawDiff) {
    if (!rawDiff) return "—";

    const diff =
        typeof rawDiff === "string"
            ? (() => {
                try {
                    return JSON.parse(rawDiff);
                } catch {
                    return null;
                }
            })()
            : rawDiff;

    if (!diff) return "—";

    if (diff.deleted) {
        return "Episode deleted";
    }

    const fields = diff.fields || {};
    const entries = Object.entries(fields);
    if (!entries.length) return "—";

    const parts = entries.map(([field, change]) => {
        const fromRaw = change?.from;
        const toRaw = change?.to;

        const stringify = (v) => {
            if (v === null || v === undefined) return "∅";
            if (typeof v === "string") return v;
            return JSON.stringify(v);
        };

        const maxLen = field === "client_review_comments" ? 200 : 40;

        const truncate = (s, max = maxLen) =>
            s.length > max ? s.slice(0, max - 1) + "…" : s;

        const fromFull = stringify(fromRaw);
        const toFull = stringify(toRaw);

        let from = truncate(fromFull, maxLen);
        let to = truncate(toFull, maxLen);

        if (from === to && fromFull !== toFull) {
            if (field === "client_review_comments") {
                return `${field}: [comment updated]`;
            }
            return `${field}: [value changed]`;
        }

        return `${field}: "${from}" → "${to}"`;
    });

    if (parts.length <= 4) {
        return parts.join(", ");
    }

    const extra = parts.length - 4;
    return parts.slice(0, 4).join(", ") + `, +${extra} more`;
}

/* --------------------------------------------
   CHANGE LOG SUMMARY (log-aware, friendly
   handling for email notifications)
-------------------------------------------- */
function summariseLog(log) {
    if (!log) return "—";

    const action = (log.action || "").toLowerCase().trim();
    const source = (log.source || "").toLowerCase().trim();
    const rawDiff = log.diff_json || log.diff || null;

    const looksLikeNotification =
        action.includes("notification") ||
        action.includes("notify") ||
        source.includes("notification") ||
        source.includes("email");

    if (!looksLikeNotification) {
        return summariseDiff(rawDiff);
    }

    let diffObj = rawDiff;
    if (typeof diffObj === "string") {
        try {
            diffObj = JSON.parse(diffObj);
        } catch {
            diffObj = null;
        }
    }

    let message = "";
    let recipients = "";

    if (diffObj && typeof diffObj === "object" && diffObj.fields) {
        const f = diffObj.fields;

        if (f.notification_message && typeof f.notification_message.to === "string") {
            message = f.notification_message.to;
        }

        if (
            f.notification_recipients &&
            typeof f.notification_recipients.to === "string"
        ) {
            recipients = f.notification_recipients.to.replace(/;\s*/g, "; ");
        }
    }

    if ((!message || !recipients) && diffObj && typeof diffObj === "object") {
        let info = null;

        if (diffObj.fields && diffObj.fields.notification) {
            const notifField = diffObj.fields.notification;
            info =
                notifField.to && typeof notifField.to === "object"
                    ? notifField.to
                    : notifField;
        } else if (diffObj.notification) {
            info = diffObj.notification;
        } else if (
            diffObj.message ||
            diffObj.body ||
            diffObj.text ||
            diffObj.recipients ||
            diffObj.email ||
            diffObj.emails
        ) {
            info = diffObj;
        }

        if (info && typeof info === "object") {
            if (!message) {
                const msgCandidate =
                    info.message ||
                    info.body ||
                    info.text ||
                    (info.to && info.to.message);

                if (msgCandidate && typeof msgCandidate === "string") {
                    message =
                        msgCandidate.length > 80
                            ? msgCandidate.slice(0, 79) + "…"
                            : msgCandidate;
                }
            }

            if (!recipients) {
                let recCandidate =
                    info.recipients ||
                    info.to ||
                    info.emails ||
                    info.email ||
                    info.to_email;

                if (Array.isArray(recCandidate)) {
                    recCandidate = recCandidate.join(", ");
                }

                if (typeof recCandidate === "string") {
                    recipients = recCandidate;
                }
            }
        }
    }

    if (message && recipients) {
        return `Email notification to ${recipients}: "${message}"`;
    }
    if (message) {
        return `Email notification sent: "${message}"`;
    }
    if (recipients) {
        return `Email notification sent to ${recipients}`;
    }

    return "Email notification sent";
}

/* --------------------------------------------
   INLINE DIFF HELPERS (for suggested changes)
   - Title: text diff → JSX spans
   - Show notes: HTML diff → HTML string
   Uses .change-log-diff__chunk--added/--removed from CSS
-------------------------------------------- */

function renderTextDiff(oldText, newText, mode = "old") {
    const a = oldText || "";
    const b = newText || "";
    const parts = diffWords(a, b);

    return parts.map((part, idx) => {
        // "old" = current side, "new" = suggested side
        if (mode === "old" && part.added) return null;
        if (mode === "new" && part.removed) return null;

        let className = "change-log-diff__chunk";

        if (part.added) {
            className += " change-log-diff__chunk--added";
        } else if (part.removed) {
            className += " change-log-diff__chunk--removed";
        } else {
            className += " change-log-diff__chunk--unchanged";
        }

        return (
            <span key={idx} className={className}>
                {part.value}
            </span>
        );
    });
}

function buildHtmlDiff(oldHtml, newHtml, mode = "old") {
    const a = oldHtml || "";
    const b = newHtml || "";
    const parts = diffWords(a, b);
    let result = "";

    for (const part of parts) {
        const value = part.value;

        if (part.added) {
            if (mode === "new") {
                // Added text → green
                result += `<span class="change-log-diff__chunk change-log-diff__chunk--added">${value}</span>`;
            }
            // skip on old side
        } else if (part.removed) {
            if (mode === "old") {
                // Removed text → red + strikethrough
                result += `<span class="change-log-diff__chunk change-log-diff__chunk--removed">${value}</span>`;
            }
            // skip on new side
        } else {
            result += value;
        }
    }

    return result;
}

function Episodes({ token, onLogout, embedded = false }) {
    const [currentUserRole, setCurrentUserRole] = useState("");
    const [episodes, setEpisodes] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [openingEpisodeId, setOpeningEpisodeId] = useState(null);
    const [openingFilesEpisodeId, setOpeningFilesEpisodeId] = useState(null);

    // Podcasts (for create flow & filters)
    const [podcasts, setPodcasts] = useState([]);
    const [podcastsError, setPodcastsError] = useState("");
    const [currentAccessLevel, setCurrentAccessLevel] = useState("write");
    const [episodeCapabilities, setEpisodeCapabilities] = useState({
        view_file_browser: false,
        episode_files_upload: false,
        review_episodes: false,
        edit_episode_metadata: false,
        edit_show_notes: false,
        upload_media: false,
        send_notifications: false,
        view_change_history: false,
    });

    // Filters
    const [statusFilter, setStatusFilter] = useState("not-published");
    const [podcastFilter, setPodcastFilter] = useState("");
    const [search, setSearch] = useState("");

    // Main episode modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFilesOnlyMode, setIsFilesOnlyMode] = useState(false);
    const [modalError, setModalError] = useState("");
    const [saving, setSaving] = useState(false);

    // Upload state (audio + artwork) for the *currently open* episode
    const [audioFile, setAudioFile] = useState(null);
    const [artworkFile, setArtworkFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [creatingSharePointFolder, setCreatingSharePointFolder] = useState(false);
    const [canCreateSharePointFolder, setCanCreateSharePointFolder] = useState(false);

    // Global media upload lock (any episode)
    const [globalMediaUploading, setGlobalMediaUploading] = useState(false);
    const [globalMediaUploadEpisodeId, setGlobalMediaUploadEpisodeId] =
        useState(null);

    // "Select podcast for new episode" modal state
    const [isSelectPodcastOpen, setIsSelectPodcastOpen] = useState(false);
    const [selectedPodcastId, setSelectedPodcastId] = useState("");
    const [selectPodcastError, setSelectPodcastError] = useState("");
    const [createAsRequested, setCreateAsRequested] = useState(false);
    const [createWithoutCaptivateSync, setCreateWithoutCaptivateSync] =
        useState(false);

    // Notifications modal state
    const [isNotifyOpen, setIsNotifyOpen] = useState(false);
    const [notifyMessage, setNotifyMessage] = useState("");
    const [notifyError, setNotifyError] = useState("");
    const [notifySuccess, setNotifySuccess] = useState("");
    const [notifying, setNotifying] = useState(false);

    // Change logs modal state
    const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);
    const [changeLogs, setChangeLogs] = useState([]);
    const [changeLogsLoading, setChangeLogsLoading] = useState(false);
    const [changeLogsError, setChangeLogsError] = useState("");

    // Suggested changes modal state
    const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
    const [suggestedChanges, setSuggestedChanges] = useState(null);
    const [hasClientSuggestedChanges, setHasClientSuggestedChanges] =
        useState(false);
    const [clearSuggestedOnSave, setClearSuggestedOnSave] = useState(false);
    const [applyTitleSuggestion, setApplyTitleSuggestion] = useState(true);
    const [applyShowNotesSuggestion, setApplyShowNotesSuggestion] =
        useState(true);
    const [isEpisodeNotesQuickOpen, setIsEpisodeNotesQuickOpen] = useState(false);
    const [episodeNotesDraft, setEpisodeNotesDraft] = useState("");
    const [episodeNotesQuickSaving, setEpisodeNotesQuickSaving] = useState(false);
    const [episodeNotesQuickError, setEpisodeNotesQuickError] = useState("");
    const [isReviewNotesPromptOpen, setIsReviewNotesPromptOpen] = useState(false);
    const [reviewNotesDraft, setReviewNotesDraft] = useState("");
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [shareEmailsDraft, setShareEmailsDraft] = useState("");
    const [shareBusy, setShareBusy] = useState(false);
    const [shareError, setShareError] = useState("");
    const [shareResult, setShareResult] = useState(null);
    const [isTranscriptPreviewOpen, setIsTranscriptPreviewOpen] = useState(false);
    const [transcribingAudio, setTranscribingAudio] = useState(false);
    const [processingAudio, setProcessingAudio] = useState(false);
    const [generatingShowNotes, setGeneratingShowNotes] = useState(false);
    const [clientEpisodeAudioUrl, setClientEpisodeAudioUrl] = useState("");
    const [clientEpisodeAudioLoading, setClientEpisodeAudioLoading] =
        useState(false);
    const [clientEpisodeAudioError, setClientEpisodeAudioError] = useState("");
    const [clientEpisodeAudioSource, setClientEpisodeAudioSource] = useState("");
    const [multitrackProcessing, setMultitrackProcessing] = useState(false);
    const [isMultitrackModalOpen, setIsMultitrackModalOpen] = useState(false);
    const [multitrackProductionName, setMultitrackProductionName] = useState("");
    const [multitrackRows, setMultitrackRows] = useState([
        createMultitrackRow(),
        createMultitrackRow(),
    ]);
    const [multitrackPresetUuid, setMultitrackPresetUuid] = useState("");
    const [multitrackDefaultPresetUuid, setMultitrackDefaultPresetUuid] =
        useState("");
    const [multitrackPresets, setMultitrackPresets] = useState([]);
    const [multitrackPresetsLoaded, setMultitrackPresetsLoaded] = useState(false);
    const [multitrackPresetsLoading, setMultitrackPresetsLoading] = useState(false);
    const [multitrackModalError, setMultitrackModalError] = useState("");
    const [isProcessAudioPresetModalOpen, setIsProcessAudioPresetModalOpen] =
        useState(false);
    const [processAudioPresetItem, setProcessAudioPresetItem] = useState(null);
    const [processAudioPresetUuid, setProcessAudioPresetUuid] = useState("");
    const [processAudioPresetError, setProcessAudioPresetError] = useState("");

    // Toasts (global page-level notifications)
    const [toasts, setToasts] = useState([]);

    // Form state for the episode being edited
    const emptyForm = {
        id: null,
        podcast_id: "",
        title: "",
        episode_number: "",
        season_number: "",
        publication_date: "",
        publication_time: "",
        status: "Planned",
        review_round_count: 0,
        notes: "",
        captivate_episode_id: "",
        show_notes_captivate: "",
        episode_type: "Normal",
        explicit: false,

        // Client review fields (read-only)
        client_review_comments: "",
        client_reviewed_at: "",
        review_send_notes: "",
        client_review_attachments: [],

        // Media meta (read-only from DB)
        episode_mp3: "",
        last_audio_uploaded_path: "",
        last_media_uploaded: "",
        episode_artwork: "",
        last_artwork_uploaded_path: "",
        last_artwork_uploaded: "",
        sharepoint_folder_path: "",
        selected_audio_file_item_id: "",
        selected_audio_file_name: "",
        selected_artwork_file_item_id: "",
        selected_artwork_file_name: "",
        audio_transcription: "",
        audio_transcription_updated_at: "",
        audio_transcription_source: "",
    };

    const [form, setForm] = useState(emptyForm);
    const initialFormRef = useRef(emptyForm);
    const [episodeFilesPath, setEpisodeFilesPath] = useState("/");
    const [episodeFilesCanWrite, setEpisodeFilesCanWrite] = useState(false);
    const [episodeFilesCanView, setEpisodeFilesCanView] = useState(false);
    const [episodeFilesCanUpload, setEpisodeFilesCanUpload] = useState(false);
    const [episodeFilesCanCreateFolder, setEpisodeFilesCanCreateFolder] =
        useState(false);
    const [episodeFilesCanRenameDelete, setEpisodeFilesCanRenameDelete] =
        useState(false);
    const [episodeFilesItems, setEpisodeFilesItems] = useState([]);
    const [episodeFilesLoading, setEpisodeFilesLoading] = useState(false);
    const [episodeFilesUploading, setEpisodeFilesUploading] = useState(false);
    const [episodeFilesUploadProgress, setEpisodeFilesUploadProgress] = useState(0);
    const [episodeFilesSortKey, setEpisodeFilesSortKey] = useState(null);
    const [episodeFilesSortDirection, setEpisodeFilesSortDirection] =
        useState("asc");
    const [activeEpisodeFileMenuId, setActiveEpisodeFileMenuId] = useState(null);
    const [episodeFilesBusyAction, setEpisodeFilesBusyAction] = useState(false);
    const [episodeFilesError, setEpisodeFilesError] = useState("");
    const [episodeFilePreviewOpen, setEpisodeFilePreviewOpen] = useState(false);
    const [episodeFilePreviewLoading, setEpisodeFilePreviewLoading] = useState(false);
    const [episodeFilePreviewError, setEpisodeFilePreviewError] = useState("");
    const [episodeFilePreviewName, setEpisodeFilePreviewName] = useState("");
    const [episodeFilePreviewType, setEpisodeFilePreviewType] = useState("");
    const [episodeFilePreviewUrl, setEpisodeFilePreviewUrl] = useState("");
    const [episodeFilesSearch, setEpisodeFilesSearch] = useState("");
    const [episodeFilesDragOver, setEpisodeFilesDragOver] = useState(false);
    const [episodeFilesFolderDropTargetId, setEpisodeFilesFolderDropTargetId] =
        useState("");
    const [episodeExternalLinks, setEpisodeExternalLinks] = useState([]);
    const [episodeExternalLinksLoading, setEpisodeExternalLinksLoading] =
        useState(false);
    const [episodeExternalLinksBusy, setEpisodeExternalLinksBusy] = useState(false);
    const [episodeExternalLinksError, setEpisodeExternalLinksError] = useState("");
    const [episodeExternalLinkTitle, setEpisodeExternalLinkTitle] = useState("");
    const [episodeExternalLinkUrl, setEpisodeExternalLinkUrl] = useState("");
    const [episodeExternalLinkNotes, setEpisodeExternalLinkNotes] = useState("");

    // Refs for forms (for keyboard shortcuts)
    const episodeFormRef = useRef(null);
    const notifyFormRef = useRef(null);
    const episodeFileInputRef = useRef(null);
    const episodeFilesDragDepthRef = useRef(0);
    const episodeFilesUploadToastIdRef = useRef(null);
    const episodeDraggedFileItemRef = useRef(null);

    // Refs for syncing show notes scroll in "Review suggested changes"
    const currentNotesScrollRef = useRef(null);
    const suggestedNotesScrollRef = useRef(null);
    const showNotesSyncingRef = useRef(false);

    // ---------- TOAST HELPERS ----------
    const addToast = (message, type = "info", timeoutMs = 5000) => {
        const id = Date.now() + Math.random();
        setToasts((current) => [...current, { id, message, type }]);

        if (timeoutMs && timeoutMs > 0) {
            window.setTimeout(() => {
                setToasts((current) => current.filter((t) => t.id !== id));
            }, timeoutMs);
        }

        return id;
    };

    const removeToast = (id) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    };

    const updateToast = (id, message, type) => {
        if (!id) return;
        setToasts((current) =>
            current.map((t) =>
                t.id === id
                    ? {
                          ...t,
                          message: message ?? t.message,
                          ...(type ? { type } : {}),
                      }
                    : t
            )
        );
    };

    // Quill toolbar
    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
        ],
    };

    const quillFormats = [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "list",
        "bullet",
        "link",
    ];

    // Warn before closing tab while work is in progress or unsaved modal changes exist.
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            const hasInFlightWork =
                globalMediaUploading ||
                episodeFilesUploading ||
                episodeFilesBusyAction ||
                saving ||
                isUploading;
            const hasUnsavedEpisodeChanges = hasUnsavedChanges();
            if (hasInFlightWork || hasUnsavedEpisodeChanges) {
                e.preventDefault();
                e.returnValue =
                    "You have unsaved changes or an in-progress action. If you leave now it may be interrupted.";
                return e;
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [
        globalMediaUploading,
        episodeFilesUploading,
        episodeFilesBusyAction,
        saving,
        isUploading,
        isModalOpen,
        form,
        audioFile,
        artworkFile,
    ]);

    // Load episodes list
    useEffect(() => {
        async function load() {
            try {
                const res = await getEpisodes();
                if (!res || !res.ok) {
                    setError(res?.message || "Failed to load episodes");
                } else {
                    setEpisodes(res.episodes || []);
                }
            } catch (err) {
                console.error("Episodes load error:", err);
                setError("Failed to load episodes");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadCurrentUserRole() {
            try {
                const res = await getCurrentUser();
                if (cancelled) return;
                if (res?.ok && res?.user?.role) {
                    setCurrentUserRole(res.user.role);
                }
            } catch (err) {
                console.error("Failed to load current user role:", err);
            }
        }

        loadCurrentUserRole();
        return () => {
            cancelled = true;
        };
    }, []);

    // Load podcasts list (for create + filters)
    useEffect(() => {
        async function loadPodcasts() {
            try {
                const res = await getPodcasts();
                if (!res || !res.ok) {
                    setPodcastsError(res?.message || "Failed to load podcasts");
                } else {
                    setPodcasts(res.podcasts || []);
                }
            } catch (err) {
                console.error("Podcasts load error:", err);
                setPodcastsError("Failed to load podcasts");
            }
        }
        loadPodcasts();
    }, []);

    // Auto-open episode modal when URL contains ?id=123
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            const queryPart = hash.split("?")[1];
            if (!queryPart || episodes.length === 0) return;

            const params = new URLSearchParams(queryPart);
            const episodeId = params.get("id");
            if (!episodeId) return;

            const ep = episodes.find((e) => String(e.id) === String(episodeId));
            if (ep) {
                handleEditClick(ep.id);
                window.location.hash = "#/episodes";
            }
        };

        handleHashChange();
        window.addEventListener("hashchange", handleHashChange);

        return () => window.removeEventListener("hashchange", handleHashChange);
    }, [episodes]);

    const resetUploadState = () => {
        setAudioFile(null);
        setArtworkFile(null);
        setIsUploading(false);
        setUploadProgress(0);
        setTranscribingAudio(false);
    };

    const resetNotifyState = () => {
        setNotifyMessage("");
        setNotifyError("");
        setNotifySuccess("");
        setNotifying(false);
    };

    const resetChangeLogState = () => {
        setChangeLogs([]);
        setChangeLogsError("");
        setChangeLogsLoading(false);
    };

    const resetSuggestedChangesState = () => {
        setIsSuggestModalOpen(false);
        setSuggestedChanges(null);
        setHasClientSuggestedChanges(false);
        setClearSuggestedOnSave(false);
        setApplyTitleSuggestion(true);
        setApplyShowNotesSuggestion(true);
    };

    const resetEpisodeNotesQuickState = () => {
        setIsEpisodeNotesQuickOpen(false);
        setEpisodeNotesDraft("");
        setEpisodeNotesQuickSaving(false);
        setEpisodeNotesQuickError("");
    };

    const resetReviewNotesPromptState = () => {
        setIsReviewNotesPromptOpen(false);
        setReviewNotesDraft("");
    };

    const resetEpisodeFilesState = () => {
        setEpisodeFilesPath("/");
        setEpisodeFilesCanWrite(false);
        setEpisodeFilesCanView(false);
        setEpisodeFilesCanUpload(false);
        setEpisodeFilesCanCreateFolder(false);
        setEpisodeFilesCanRenameDelete(false);
        setEpisodeFilesItems([]);
        setEpisodeFilesLoading(false);
        setEpisodeFilesUploading(false);
        setEpisodeFilesUploadProgress(0);
        setEpisodeFilesBusyAction(false);
        setEpisodeFilesError("");
        setEpisodeFilesSearch("");
        setEpisodeFilesDragOver(false);
        setEpisodeExternalLinks([]);
        setEpisodeExternalLinksLoading(false);
        setEpisodeExternalLinksBusy(false);
        setEpisodeExternalLinksError("");
        setEpisodeExternalLinkTitle("");
        setEpisodeExternalLinkUrl("");
        setEpisodeExternalLinkNotes("");
        setIsMultitrackModalOpen(false);
        setMultitrackProductionName("");
        setMultitrackRows([createMultitrackRow(), createMultitrackRow()]);
        setMultitrackModalError("");
        setIsProcessAudioPresetModalOpen(false);
        setProcessAudioPresetItem(null);
        setProcessAudioPresetUuid("");
        setProcessAudioPresetError("");
        episodeFilesDragDepthRef.current = 0;
    };

    const loadEpisodeFiles = async (episodeId, path = "/") => {
        if (!episodeId) return;
        setEpisodeFilesLoading(true);
        setEpisodeFilesError("");
        setEpisodeFilesCanWrite(false);
        setEpisodeFilesCanUpload(false);
        setEpisodeFilesCanCreateFolder(false);
        setEpisodeFilesCanRenameDelete(false);
        try {
            const res = await listEpisodeFiles(episodeId, path);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to load episode files");
            }

            setEpisodeFilesItems(Array.isArray(res.items) ? res.items : []);
            setEpisodeFilesPath(res.path || "/");
            setEpisodeFilesCanWrite(Boolean(res.can_write));
            setEpisodeFilesCanView(true);
            setEpisodeFilesCanUpload(Boolean(res.can_upload));
            setEpisodeFilesCanCreateFolder(Boolean(res.can_create_folder));
            setEpisodeFilesCanRenameDelete(Boolean(res.can_rename_delete));
        } catch (err) {
            setEpisodeFilesError(err.message || "Failed to load episode files");
            setEpisodeFilesCanView(false);
            setEpisodeFilesItems([]);
        } finally {
            setEpisodeFilesLoading(false);
        }
    };

    const loadEpisodeExternalLinks = async (episodeId) => {
        if (!episodeId) return;
        setEpisodeExternalLinksLoading(true);
        setEpisodeExternalLinksError("");
        try {
            const res = await listEpisodeExternalLinks(episodeId);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to load external links");
            }
            setEpisodeExternalLinks(Array.isArray(res.links) ? res.links : []);
        } catch (err) {
            setEpisodeExternalLinksError(err.message || "Failed to load external links");
            setEpisodeExternalLinks([]);
        } finally {
            setEpisodeExternalLinksLoading(false);
        }
    };

    const selectedPodcastName =
        podcasts.find((p) => String(p.id) === String(podcastFilter))?.name || "";
    const selectedFormPodcast = podcasts.find(
        (p) => String(p.id) === String(form.podcast_id)
    );
    const selectedFormPodcastHasCaptivateShow = Boolean(
        String(selectedFormPodcast?.captivate_show_id || "").trim()
    );

    const creatablePodcastIds = new Set(
        podcasts
            .filter((p) => {
                if (!currentUserRole) return false;
                if (currentUserRole !== "client") return true;
                return Number(p?.create_episodes ?? 0) === 1;
            })
            .map((p) => String(p.id))
    );

    const canWriteCurrentEpisode =
        (currentAccessLevel || "").toLowerCase() === "write";

    // Derived + filtered list
    const visibleEpisodes = [...episodes]
        .filter((ep) => {
            const s = (ep.status || "").toLowerCase().trim();

            if (statusFilter === "not-published") {
                if (s === "published") return false;
                if (currentUserRole !== "client" && s === "approved") {
                    const datePart = String(ep?.publication_date || "").slice(0, 10);
                    if (!datePart) return true;
                    const approvedDate = new Date(`${datePart}T00:00:00`);
                    if (Number.isNaN(approvedDate.getTime())) return true;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (approvedDate < today) return false;
                }
            } else if (statusFilter !== "all") {
                if (s !== statusFilter.toLowerCase()) return false;
            }

            if (podcastFilter && selectedPodcastName) {
                if ((ep.podcast_name || "") !== selectedPodcastName) return false;
            }

            if (search.trim()) {
                const term = search.trim().toLowerCase();
                const title = (ep.title || "").toLowerCase();
                if (!title.includes(term)) return false;
            }

            return true;
        })
        .sort((a, b) => {
            const toTimestamp = (ep) => {
                const datePart = String(ep?.publication_date || "").slice(0, 10);
                const timePart = String(ep?.publication_time || "").trim().slice(0, 5);
                if (!datePart) return null;
                const iso = `${datePart}T${timePart || "00:00"}:00`;
                const ts = new Date(iso).getTime();
                return Number.isNaN(ts) ? null : ts;
            };

            const ta = toTimestamp(a);
            const tb = toTimestamp(b);

            if (
                statusFilter === "published" ||
                (currentUserRole === "client" && statusFilter === "all")
            ) {
                if (ta === null && tb === null) return 0;
                if (ta === null) return 1;
                if (tb === null) return -1;
                return tb - ta;
            }

            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
        });

    // ---------- UNSAVED CHANGES + CLOSE HELPERS ----------

    // Normalise values before comparing for unsaved changes
    const normaliseForDirtyCheck = (key, value) => {
        // Treat null/undefined as empty string across the board
        if (value === null || value === undefined) {
            value = "";
        }

        // --- Quill / show notes HTML ---
        if (key === "show_notes_captivate") {
            let v = String(value);

            // Normalise newlines
            v = v.replace(/\r\n/g, "\n");
            v = v.replace(/\n+/g, "\n");

            // Collapse whitespace between tags: "<p>Text</p>\n  <p>Next</p>" → "<p>Text</p><p>Next</p>"
            v = v.replace(/>\s+</g, "><");

            // Trim overall whitespace
            v = v.trim();

            // Treat these as "no content"
            const emptyPatterns = [
                "",
                "<p><br></p>",
                "<p><br></p>\n",
                "<p></p>",
                "<p></p>\n",
            ];
            if (emptyPatterns.includes(v)) {
                return "";
            }

            return v;
        }

        // --- Time: treat "11:00" and "11:00:00" (or "1100") the same ---
        if (key === "publication_time") {
            let v = String(value).trim();
            if (!v) return "";

            // If "0000" → "00:00"
            if (/^\d{4}$/.test(v)) {
                v = `${v.slice(0, 2)}:${v.slice(2, 4)}`;
            }

            // If "HH:MM" → "HH:MM:00"
            if (/^\d{2}:\d{2}$/.test(v)) {
                v = `${v}:00`;
            }

            return v;
        }

        // Light normalisation for common text fields
        if (key === "notes" || key === "title") {
            return String(value).trim();
        }

        // Default: leave as-is
        return value;
    };

    function hasUnsavedChanges() {
        if (!isModalOpen) return false;

        const initial = initialFormRef.current || emptyForm;

        for (const key of Object.keys(initial)) {
            const original = normaliseForDirtyCheck(key, initial[key]);
            const current = normaliseForDirtyCheck(key, form[key]);

            if (current !== original) {
                return true;
            }
        }

        if (audioFile || artworkFile) return true;

        return false;
    }

    const forceCloseModal = () => {
        setIsModalOpen(false);
        setIsFilesOnlyMode(false);
        setIsShareOpen(false);
        setIsTranscriptPreviewOpen(false);
        setShareBusy(false);
        setShareError("");
        setShareResult(null);
        setShareEmailsDraft("");
        setModalError("");
        setCreatingSharePointFolder(false);
        setCanCreateSharePointFolder(false);
        setCreateAsRequested(false);
        setCreateWithoutCaptivateSync(false);
        setOpeningEpisodeId(null);
        setOpeningFilesEpisodeId(null);
        setForm(emptyForm);
        setEpisodeCapabilities({
            view_file_browser: false,
            episode_files_upload: false,
            review_episodes: false,
            edit_episode_metadata: false,
            edit_show_notes: false,
            upload_media: false,
            send_notifications: false,
            view_change_history: false,
        });
        initialFormRef.current = emptyForm;
        resetUploadState();
        resetEpisodeFilesState();

        setIsNotifyOpen(false);
        resetNotifyState();

        setIsChangeLogOpen(false);
        resetChangeLogState();

        resetSuggestedChangesState();
        resetEpisodeNotesQuickState();
        resetReviewNotesPromptState();
    };

    const closeModal = () => {
        if (hasUnsavedChanges()) {
            const ok = window.confirm(
                "You have unsaved changes. Close without saving?"
            );
            if (!ok) return;
        }
        forceCloseModal();
    };

    const openShareModal = () => {
        if (!form.id) return;
        setShareError("");
        setShareResult(null);
        setShareEmailsDraft("");
        setIsShareOpen(true);
    };

    const closeShareModal = () => {
        if (shareBusy) return;
        setIsShareOpen(false);
        setShareError("");
        setShareResult(null);
        setShareEmailsDraft("");
    };

    const openTranscriptPreviewModal = () => {
        if (!String(form.audio_transcription || "").trim()) return;
        setIsTranscriptPreviewOpen(true);
    };

    const closeTranscriptPreviewModal = () => {
        setIsTranscriptPreviewOpen(false);
    };

    const handleCreateShare = async () => {
        const episodeId = Number(form.id || 0);
        if (!episodeId) return;
        setShareBusy(true);
        setShareError("");
        setShareResult(null);
        try {
            const res = await createEpisodeShare(episodeId, shareEmailsDraft);
            if (!res?.ok) {
                setShareError(res?.message || "Failed to create share link");
                return;
            }
            setShareResult(res);
        } catch (err) {
            setShareError(err?.message || "Failed to create share link");
        } finally {
            setShareBusy(false);
        }
    };

    const handleSendShareEmails = async () => {
        const shareToken = String(shareResult?.token || "").trim();
        if (!shareToken) return;
        setShareBusy(true);
        setShareError("");
        try {
            const res = await sendEpisodeShareEmails(shareToken, shareEmailsDraft);
            if (!res?.ok) {
                setShareError(res?.message || "Failed to send share email request");
                return;
            }
            setShareResult((prev) =>
                prev
                    ? {
                        ...prev,
                        shared_with_emails: res.shared_with_emails || prev.shared_with_emails || "",
                    }
                    : prev
            );
            addToast("Share email request sent", "success", 2500);
        } catch (err) {
            setShareError(err?.message || "Failed to send share email request");
        } finally {
            setShareBusy(false);
        }
    };

    const openEpisodeNotesQuickModal = () => {
        setEpisodeNotesQuickError("");
        setEpisodeNotesDraft(form.notes || "");
        setIsEpisodeNotesQuickOpen(true);
    };

    const closeEpisodeNotesQuickModal = () => {
        if (episodeNotesQuickSaving) return;
        setIsEpisodeNotesQuickOpen(false);
        setEpisodeNotesQuickError("");
    };

    const saveEpisodeNotesQuickModal = async () => {
        const episodeId = Number(form.id || 0);
        if (!episodeId) return;

        const canEditNotesNow =
            currentUserRole === "admin" ||
            Boolean(episodeCapabilities.episode_files_upload);
        if (!canEditNotesNow) {
            setIsEpisodeNotesQuickOpen(false);
            return;
        }

        setEpisodeNotesQuickSaving(true);
        setEpisodeNotesQuickError("");
        try {
            const nextNotes = (episodeNotesDraft || "").trim();
            const res = await updateEpisode(episodeId, {
                notes: nextNotes || null,
            });
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to save episode notes");
            }

            setForm((prev) => ({ ...prev, notes: nextNotes }));
            initialFormRef.current = {
                ...(initialFormRef.current || emptyForm),
                notes: nextNotes,
            };
            setIsEpisodeNotesQuickOpen(false);
        } catch (err) {
            setEpisodeNotesQuickError(err.message || "Failed to save episode notes");
        } finally {
            setEpisodeNotesQuickSaving(false);
        }
    };

    // Open modal for editing an existing episode
    const handleEditClick = async (episodeId) => {
        setOpeningEpisodeId(episodeId);
        setOpeningFilesEpisodeId(null);
        setModalError("");
        setSaving(false);
        setIsFilesOnlyMode(false);
        setCreatingSharePointFolder(false);
        setCanCreateSharePointFolder(false);
        resetUploadState();
        resetEpisodeFilesState();

        setIsNotifyOpen(false);
        resetNotifyState();

        setIsChangeLogOpen(false);
        resetChangeLogState();

        resetSuggestedChangesState();
        resetReviewNotesPromptState();
        const baseForm = { ...emptyForm, id: episodeId };
        setForm(baseForm);
        setEpisodeCapabilities({
            view_file_browser: false,
            episode_files_upload: false,
            review_episodes: false,
            edit_episode_metadata: false,
            edit_show_notes: false,
            upload_media: false,
            send_notifications: false,
            view_change_history: false,
        });
        initialFormRef.current = baseForm;

        try {
            const res = await getEpisode(episodeId);
            if (!res || !res.ok) {
                addToast(
                    res?.message || "Failed to load episode details",
                    "error",
                    6000
                );
                return;
            }

            const ep = res.episode;
            const accessLevelFromDetail = (ep.access_level || "").toLowerCase();
            const fallbackListAccess = (
                episodes.find((row) => String(row.id) === String(episodeId))
                    ?.access_level || ""
            ).toLowerCase();
            const effectiveAccessLevel =
                accessLevelFromDetail || fallbackListAccess || "write";
            setCurrentAccessLevel(effectiveAccessLevel);
            setEpisodeCapabilities({
                view_file_browser:
                    ep?.capabilities?.view_file_browser !== false,
                episode_files_upload:
                    ep?.capabilities?.episode_files_upload !== false,
                review_episodes: ep?.capabilities?.review_episodes !== false,
                edit_episode_metadata:
                    ep?.capabilities?.edit_episode_metadata !== false,
                edit_show_notes: ep?.capabilities?.edit_show_notes !== false,
                upload_media: ep?.capabilities?.upload_media !== false,
                send_notifications: ep?.capabilities?.send_notifications !== false,
                view_change_history: ep?.capabilities?.view_change_history !== false,
            });
            setCanCreateSharePointFolder(
                Boolean(ep?.can_create_sharepoint_folder)
            );

            const nextForm = {
                id: ep.id,
                podcast_id: ep.podcast_id ?? "",
                title: ep.title ?? "",
                episode_number: ep.episode_number ?? "",
                season_number: ep.season_number ?? "",
                publication_date: ep.publication_date
                    ? String(ep.publication_date).slice(0, 10)
                    : "",
                publication_time: ep.publication_time ?? "",
                status: ep.status || "Planned",
                review_round_count: Number(ep.review_round_count || 0),
                notes: ep.notes ?? "",
                captivate_episode_id: ep.captivate_episode_id ?? "",
                show_notes_captivate: ep.show_notes_captivate ?? "",
                episode_type: ep.episode_type || "Normal",
                explicit: !!ep.explicit,
                client_review_comments: ep.client_review_comments ?? "",
                client_reviewed_at: ep.client_reviewed_at || "",
                review_send_notes: ep.review_send_notes ?? "",
                client_review_attachments: Array.isArray(ep.client_review_attachments)
                    ? ep.client_review_attachments
                    : [],

                episode_mp3: ep.episode_mp3 ?? "",
                last_audio_uploaded_path: ep.last_audio_uploaded_path ?? "",
                last_media_uploaded: ep.last_media_uploaded || "",
                episode_artwork: ep.episode_artwork ?? "",
                last_artwork_uploaded_path: ep.last_artwork_uploaded_path ?? "",
                last_artwork_uploaded: ep.last_artwork_uploaded || "",
                sharepoint_folder_path: ep.sharepoint_folder_path ?? "",
                selected_audio_file_item_id: ep.selected_audio_file_item_id ?? "",
                selected_audio_file_name: ep.selected_audio_file_name ?? "",
                selected_artwork_file_item_id: ep.selected_artwork_file_item_id ?? "",
                selected_artwork_file_name: ep.selected_artwork_file_name ?? "",
                audio_transcription: ep.audio_transcription ?? "",
                audio_transcription_updated_at: ep.audio_transcription_updated_at || "",
                audio_transcription_source: ep.audio_transcription_source || "",
            };

            setForm(nextForm);
            initialFormRef.current = nextForm;

            // Suggested changes from client (title + show notes only)
            let suggested = ep.client_suggested_changes_parsed || null;
            let hasSuggestions = false;

            if (suggested && typeof suggested === "object") {
                const suggestedTitle =
                    typeof suggested.title === "string" ? suggested.title.trim() : "";
                const suggestedNotes =
                    typeof suggested.show_notes_captivate === "string"
                        ? suggested.show_notes_captivate.trim()
                        : "";

                const hasTitle =
                    suggestedTitle && suggestedTitle !== (ep.title || "");
                const hasNotes =
                    suggestedNotes &&
                    suggestedNotes !== (ep.show_notes_captivate || "");

                if (hasTitle || hasNotes) {
                    hasSuggestions = true;
                } else {
                    suggested = null;
                }
            } else {
                suggested = null;
            }

            setSuggestedChanges(suggested);
            setHasClientSuggestedChanges(hasSuggestions);
            setClearSuggestedOnSave(false);
            setApplyTitleSuggestion(true);
            setApplyShowNotesSuggestion(true);

            if (ep.sharepoint_folder_path) {
                await Promise.all([
                    loadEpisodeFiles(ep.id, "/"),
                    loadEpisodeExternalLinks(ep.id),
                ]);
            }

            setIsModalOpen(true);
        } catch (err) {
            console.error("Load episode detail error:", err);
            addToast("Failed to load episode details", "error", 6000);
        } finally {
            setOpeningEpisodeId(null);
        }
    };

    const handleCreateMissingSharePointFolder = async () => {
        if (!form.id) return;
        if (!isAdminUser) return;
        if (!canCreateSharePointFolder) return;
        if (String(form.sharepoint_folder_path || "").trim()) return;

        setCreatingSharePointFolder(true);
        setModalError("");
        try {
            const res = await createEpisodeSharePointFolder(form.id);
            if (!res || !res.ok) {
                throw new Error(
                    res?.message || "Failed to create SharePoint folder for this episode"
                );
            }

            const createdPath = String(res.sharepoint_folder_path || "").trim();
            if (createdPath) {
                setForm((prev) => ({ ...prev, sharepoint_folder_path: createdPath }));
                await Promise.all([
                    loadEpisodeFiles(form.id, "/"),
                    loadEpisodeExternalLinks(form.id),
                ]);
            }
            setCanCreateSharePointFolder(false);
        } catch (err) {
            setModalError(
                err?.message || "Failed to create SharePoint folder for this episode"
            );
        } finally {
            setCreatingSharePointFolder(false);
        }
    };

    const handleOpenFilesClick = async (ep) => {
        const episodeId = Number(ep?.id || 0);
        if (!episodeId) return;
        setOpeningEpisodeId(null);
        setOpeningFilesEpisodeId(episodeId);

        setModalError("");
        setSaving(false);
        setIsFilesOnlyMode(true);
        resetUploadState();
        resetEpisodeFilesState();

        setIsNotifyOpen(false);
        resetNotifyState();

        setIsChangeLogOpen(false);
        resetChangeLogState();

        resetSuggestedChangesState();
        resetReviewNotesPromptState();
        const baseForm = {
            ...emptyForm,
            id: episodeId,
            title: ep?.title ?? "",
            sharepoint_folder_path: ep?.sharepoint_folder_path ?? "",
        };
        setForm(baseForm);
        initialFormRef.current = baseForm;

        try {
            const detailRes = await getEpisode(episodeId);
            if (detailRes?.ok && detailRes?.episode) {
                const detailed = detailRes.episode;
                setForm((prev) => ({
                    ...prev,
                    selected_audio_file_item_id:
                        detailed.selected_audio_file_item_id ?? "",
                    selected_audio_file_name:
                        detailed.selected_audio_file_name ?? "",
                    selected_artwork_file_item_id:
                        detailed.selected_artwork_file_item_id ?? "",
                    selected_artwork_file_name:
                        detailed.selected_artwork_file_name ?? "",
                }));
                initialFormRef.current = {
                    ...initialFormRef.current,
                    selected_audio_file_item_id:
                        detailed.selected_audio_file_item_id ?? "",
                    selected_audio_file_name:
                        detailed.selected_audio_file_name ?? "",
                    selected_artwork_file_item_id:
                        detailed.selected_artwork_file_item_id ?? "",
                    selected_artwork_file_name:
                        detailed.selected_artwork_file_name ?? "",
                };
            }
            await Promise.all([
                loadEpisodeFiles(episodeId, "/"),
                loadEpisodeExternalLinks(episodeId),
            ]);
            setIsModalOpen(true);
        } catch (err) {
            addToast(err?.message || "Failed to load episode files", "error", 6000);
        } finally {
            setOpeningFilesEpisodeId(null);
        }
    };

    // ----- CREATE EPISODE FLOW -----

    const handleOpenCreate = () => {
        if (creatablePodcastIds.size === 0) {
            addToast(
                "You do not have permission to create episodes for any podcasts.",
                "warning",
                6000
            );
            return;
        }

        setSelectPodcastError("");
        setSelectedPodcastId("");
        setCreateAsRequested(false);
        setCreateWithoutCaptivateSync(false);
        setCurrentAccessLevel("write");
        resetUploadState();
        resetEpisodeFilesState();

        setIsNotifyOpen(false);
        resetNotifyState();

        setIsChangeLogOpen(false);
        resetChangeLogState();

        resetSuggestedChangesState();
        resetReviewNotesPromptState();

        setIsSelectPodcastOpen(true);
    };

    const handleConfirmPodcast = () => {
        if (!selectedPodcastId) {
            setSelectPodcastError("Please select a podcast");
            return;
        }

        if (!creatablePodcastIds.has(String(selectedPodcastId))) {
            setSelectPodcastError(
                "You do not have permission to create episodes for the selected podcast."
            );
            return;
        }

        setIsSelectPodcastOpen(false);
        setModalError("");
        resetUploadState();
        resetEpisodeFilesState();

        setIsNotifyOpen(false);
        resetNotifyState();

        setIsChangeLogOpen(false);
        resetChangeLogState();

        resetSuggestedChangesState();
        resetReviewNotesPromptState();

        const nextForm = {
            ...emptyForm,
            podcast_id: selectedPodcastId,
            status: currentUserRole === "client" ? "Requested" : "Planned",
        };

        setCreateAsRequested(currentUserRole === "client");
        setCreateWithoutCaptivateSync(false);
        setForm(nextForm);
        setCurrentAccessLevel("write");
        setEpisodeCapabilities({
            view_file_browser: true,
            episode_files_upload: true,
            review_episodes: true,
            edit_episode_metadata: true,
            edit_show_notes: true,
            upload_media: true,
            send_notifications: true,
            view_change_history: true,
        });
        initialFormRef.current = nextForm;
        setIsModalOpen(true);
    };

    const handleCancelSelectPodcast = () => {
        setIsSelectPodcastOpen(false);
        setSelectPodcastError("");
        setSelectedPodcastId("");
        setCreateAsRequested(false);
        setCreateWithoutCaptivateSync(false);
    };

    // Generic change handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setForm((f) => ({ ...f, [name]: checked }));
    };

    const handleShowNotesChange = (value) => {
        setForm((f) => ({
            ...f,
            show_notes_captivate: value,
        }));
    };

    const handleAudioFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        setAudioFile(file);
        if (file) {
            setForm((prev) => ({
                ...prev,
                selected_audio_file_item_id: "",
                selected_audio_file_name: "",
            }));
        }
    };

    const handleArtworkFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        setArtworkFile(file);
        if (file) {
            setForm((prev) => ({
                ...prev,
                selected_artwork_file_item_id: "",
                selected_artwork_file_name: "",
            }));
        }
    };

    const handleTranscribeEpisodeFile = async (item) => {
        if (!form.id || !item || item.type !== "file") return false;
        if (!isAudioFile(item) && !isVideoFile(item)) return false;
        if (!isAdminUser) return false;
        if (transcribingAudio) return false;

        setModalError("");
        const progressToastId = addToast("Transcribing media…", "info", 0);
        setTranscribingAudio(true);
        try {
            const res = await transcribeEpisodeAudio(form.id, String(item.id || ""));
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to transcribe media");
            }
            if (res.transcription_persisted_to_episode_field === false) {
                const transcriptFileName = String(res?.transcript_file?.name || "").trim();
                addToast(
                    transcriptFileName
                        ? `Media transcription completed. Saved to episode files as ${transcriptFileName}.`
                        : "Media transcription completed.",
                    "success",
                    5000
                );
            } else {
                setForm((prev) => ({
                    ...prev,
                    audio_transcription: String(res.transcription || ""),
                    audio_transcription_updated_at: new Date().toISOString(),
                    audio_transcription_source: String(res.transcription_source || ""),
                }));
                addToast("Audio transcription completed.", "success", 4000);
            }
            return true;
        } catch (err) {
            setModalError(err.message || "Failed to transcribe media");
            addToast(err.message || "Media transcription failed.", "error", 6000);
            return false;
        } finally {
            setTranscribingAudio(false);
            removeToast(progressToastId);
        }
    };

    const handleProcessEpisodeFile = async (item, presetUuid = "") => {
        if (!form.id || !item || item.type !== "file") return false;
        if (!isAudioFile(item)) return false;
        if (!isAdminUser) return false;
        if (processingAudio) return false;

        setModalError("");
        const progressToastId = addToast("Processing audio with Auphonic…", "info", 0);
        setProcessingAudio(true);
        try {
            const res = await processEpisodeAudio(
                form.id,
                String(item.id || ""),
                String(presetUuid || "").trim()
            );
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to process audio");
            }
            const uploadedCount = Number(res?.processed_file_count || 0);
            addToast(
                uploadedCount > 0
                    ? `Audio processing completed. ${uploadedCount} file${uploadedCount === 1 ? "" : "s"} added to the episode folder.`
                    : "Audio processing completed.",
                "success",
                5000
            );
            if (form.id) {
                await loadEpisodeFiles(form.id, episodeFilesPath || "/");
            }
            return true;
        } catch (err) {
            setModalError(err.message || "Failed to process audio");
            addToast(err.message || "Audio processing failed.", "error", 6000);
            return false;
        } finally {
            setProcessingAudio(false);
            removeToast(progressToastId);
        }
    };

    const ensureAuphonicPresetOptions = async () => {
        if (multitrackPresetsLoaded) {
            return {
                defaultPreset: String(multitrackDefaultPresetUuid || "").trim(),
                presets: multitrackPresets,
            };
        }
        setMultitrackPresetsLoading(true);
        try {
            const [settings, presets] = await Promise.all([
                getShowNotesSettings(),
                getAuphonicPresets(),
            ]);
            const loadedPresets = Array.isArray(presets) ? presets : [];
            const defaultPreset = String(
                settings?.auphonic_processing_preset || ""
            ).trim();
            setMultitrackPresets(loadedPresets);
            setMultitrackDefaultPresetUuid(defaultPreset);
            setMultitrackPresetsLoaded(true);
            return { defaultPreset, presets: loadedPresets };
        } finally {
            setMultitrackPresetsLoading(false);
        }
    };

    const closeProcessAudioPresetModal = () => {
        if (processingAudio) return;
        setIsProcessAudioPresetModalOpen(false);
        setProcessAudioPresetItem(null);
        setProcessAudioPresetError("");
    };

    const handleOpenProcessAudioPresetModal = async (item) => {
        if (!form.id || !item || item.type !== "file") return;
        if (!isAudioFile(item)) return;
        if (!isAdminUser || processingAudio || transcribingAudio || generatingShowNotes) {
            return;
        }
        setProcessAudioPresetError("");
        setProcessAudioPresetItem(item);
        setProcessAudioPresetUuid(String(multitrackDefaultPresetUuid || "").trim());
        setIsProcessAudioPresetModalOpen(true);
        try {
            const { defaultPreset } = await ensureAuphonicPresetOptions();
            setProcessAudioPresetUuid(defaultPreset);
        } catch (err) {
            setProcessAudioPresetError(
                err?.message || "Failed to load Auphonic preset options"
            );
            setProcessAudioPresetUuid("");
        }
    };

    const handleSubmitProcessAudioPreset = async () => {
        if (!processAudioPresetItem) return;
        const presetUuid = String(processAudioPresetUuid || "").trim();
        if (!presetUuid) {
            setProcessAudioPresetError("Please choose an Auphonic preset.");
            return;
        }
        setIsProcessAudioPresetModalOpen(false);
        setProcessAudioPresetError("");
        await handleProcessEpisodeFile(processAudioPresetItem, presetUuid);
        setProcessAudioPresetItem(null);
    };

    const handleGenerateShowNotesFromTranscript = async (options = {}) => {
        const skipLocalTranscriptCheck = Boolean(options?.skipLocalTranscriptCheck);
        const episodeId = Number(form.id || 0);
        if (!episodeId) return false;
        if (!isAdminUser) return false;
        if (!skipLocalTranscriptCheck && !String(form.audio_transcription || "").trim()) {
            return false;
        }
        if (generatingShowNotes) return false;

        setModalError("");
        const progressToastId = addToast("Generating show notes…", "info", 0);
        setGeneratingShowNotes(true);
        try {
            const res = await generateEpisodeShowNotes(episodeId);
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to generate show notes");
            }
            const nextShowNotes = String(res.show_notes_captivate || "");
            setForm((prev) => ({
                ...prev,
                show_notes_captivate: nextShowNotes,
            }));
            initialFormRef.current = {
                ...(initialFormRef.current || emptyForm),
                show_notes_captivate: nextShowNotes,
            };
            addToast("Show notes generated from transcript.", "success", 4500);
            return true;
        } catch (err) {
            setModalError(err.message || "Failed to generate show notes");
            addToast(err.message || "Failed to generate show notes", "error", 6000);
            return false;
        } finally {
            setGeneratingShowNotes(false);
            removeToast(progressToastId);
        }
    };

    const handleTranscribeAndGenerateShowNotesFromEpisodeFile = async (item) => {
        if (!form.id || !item || item.type !== "file") return;
        if (!isAudioFile(item)) return;
        if (!isAdminUser) return;
        if (transcribingAudio || generatingShowNotes || processingAudio) return;

        const transcribed = await handleTranscribeEpisodeFile(item);
        if (!transcribed) return;
        await handleGenerateShowNotesFromTranscript({
            skipLocalTranscriptCheck: true,
        });
    };

    const closeMultitrackModal = () => {
        if (multitrackProcessing) return;
        setIsMultitrackModalOpen(false);
        setMultitrackModalError("");
    };

    const handleOpenMultitrackModal = async () => {
        if (!isAdminUser || !form.id) return;
        setMultitrackModalError("");
        setMultitrackProductionName(String(form.title || "").trim());
        setMultitrackRows([createMultitrackRow(), createMultitrackRow()]);
        setMultitrackPresetUuid(String(multitrackDefaultPresetUuid || "").trim());
        setIsMultitrackModalOpen(true);

        try {
            const { defaultPreset } = await ensureAuphonicPresetOptions();
            setMultitrackPresetUuid(defaultPreset);
        } catch (err) {
            setMultitrackModalError(
                err?.message || "Failed to load Auphonic preset options"
            );
            setMultitrackPresets([]);
            setMultitrackPresetUuid("");
        }
    };

    const handleAddMultitrackRow = () => {
        setMultitrackRows((prev) => [...prev, createMultitrackRow()]);
    };

    const handleRemoveMultitrackRow = (rowId) => {
        setMultitrackRows((prev) => {
            if (prev.length <= 2) return prev;
            return prev.filter((row) => row.row_id !== rowId);
        });
    };

    const handleMultitrackRowFileChange = (rowId, itemId) => {
        const selectedId = String(itemId || "").trim();
        setMultitrackRows((prev) =>
            prev.map((row) => {
                if (row.row_id !== rowId) return row;
                const selectedItem = episodeFilesItems.find(
                    (item) => String(item.id || "") === selectedId
                );
                const fileNameBase = splitFileNameAndExtension(
                    String(selectedItem?.name || "")
                ).baseName;
                return {
                    ...row,
                    item_id: selectedId,
                    track_name: String(row.track_name || "").trim()
                        ? row.track_name
                        : fileNameBase || row.track_name,
                };
            })
        );
    };

    const handleMultitrackRowNameChange = (rowId, trackName) => {
        setMultitrackRows((prev) =>
            prev.map((row) =>
                row.row_id === rowId ? { ...row, track_name: trackName } : row
            )
        );
    };

    const handleSubmitMultitrackProduction = async () => {
        if (!isAdminUser) return;
        if (!form.id) return;

        const productionName = String(multitrackProductionName || "").trim();
        if (!productionName) {
            setMultitrackModalError("Production name is required.");
            return;
        }
        const normalizedRows = multitrackRows.map((row) => ({
            row_id: row.row_id,
            item_id: String(row.item_id || "").trim(),
            track_name: String(row.track_name || "").trim(),
        }));
        const hasPartiallyCompletedRow = normalizedRows.some(
            (row) => (row.item_id && !row.track_name) || (!row.item_id && row.track_name)
        );
        if (hasPartiallyCompletedRow) {
            setMultitrackModalError(
                "Each added track row must include both a file and a track name."
            );
            return;
        }
        const validRows = normalizedRows.filter(
            (row) => row.item_id && row.track_name
        );
        if (validRows.length < 2) {
            setMultitrackModalError(
                "Please select at least 2 files and give each track a name."
            );
            return;
        }
        const trackIdSet = new Set(validRows.map((row) => row.item_id));
        if (trackIdSet.size !== validRows.length) {
            setMultitrackModalError("Each track must use a different file.");
            return;
        }
        const presetUuid = String(multitrackPresetUuid || "").trim();
        if (!presetUuid) {
            setMultitrackModalError("Please choose an Auphonic preset.");
            return;
        }

        setIsMultitrackModalOpen(false);
        setMultitrackModalError("");
        const progressToastId = addToast(
            "Creating multitrack production…",
            "info",
            0
        );
        setMultitrackProcessing(true);
        try {
            const res = await processEpisodeMultitrack(form.id, {
                production_name: productionName,
                tracks: validRows.map((row) => ({
                    item_id: row.item_id,
                    track_name: row.track_name,
                })),
                preset_uuid: presetUuid,
            });
            if (!res?.ok) {
                throw new Error(
                    res?.message || "Failed to create multitrack production"
                );
            }
            const uploadedCount = Number(res?.processed_file_count || 0);
            addToast(
                uploadedCount > 0
                    ? `Multitrack production completed. ${uploadedCount} file${uploadedCount === 1 ? "" : "s"} added to the episode folder.`
                    : "Multitrack production completed.",
                "success",
                5500
            );
            await loadEpisodeFiles(form.id, episodeFilesPath || "/");
        } catch (err) {
            addToast(
                err?.message || "Multitrack production failed.",
                "error",
                6500
            );
        } finally {
            setMultitrackProcessing(false);
            removeToast(progressToastId);
            setMultitrackRows([createMultitrackRow(), createMultitrackRow()]);
        }
    };

    const episodeFilesBreadcrumbs = breadcrumbSegments(episodeFilesPath);
    const currentFolderAudioFiles = episodeFilesItems.filter(
        (item) => item.type === "file" && isAudioFile(item)
    );
    const episodeVisibleFiles = [...episodeFilesItems]
        .filter((item) => {
            if (!episodeFilesSearch.trim()) return true;
            return String(item.name || "")
                .toLowerCase()
                .includes(episodeFilesSearch.trim().toLowerCase());
        })
        .sort((a, b) => {
            const effectiveSortKey = episodeFilesSortKey || "name";
            const effectiveSortDirection = episodeFilesSortKey
                ? episodeFilesSortDirection
                : "asc";
            const compareValues = (left, right) => {
                if (left === right) return 0;
                if (left === null || left === undefined || left === "") return 1;
                if (right === null || right === undefined || right === "") return -1;
                if (typeof left === "number" && typeof right === "number") {
                    return left - right;
                }
                return String(left).localeCompare(String(right), undefined, {
                    numeric: true,
                    sensitivity: "base",
                });
            };

            const getSortValue = (item) => {
                if (effectiveSortKey === "size") return Number(item.size || 0);
                if (effectiveSortKey === "modified") {
                    const ts = item.last_modified ? new Date(item.last_modified).getTime() : 0;
                    return Number.isFinite(ts) ? ts : 0;
                }
                return String(item.name || "");
            };

            if (a.type !== b.type) {
                return a.type === "folder" ? -1 : 1;
            }
            const left = getSortValue(a);
            const right = getSortValue(b);
            const base = compareValues(left, right);
            if (base !== 0) {
                return effectiveSortDirection === "asc" ? base : -base;
            }
            return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
                numeric: true,
                sensitivity: "base",
            });
        });

    const toggleEpisodeFilesSort = (key) => {
        if (episodeFilesSortKey !== key) {
            setEpisodeFilesSortKey(key);
            setEpisodeFilesSortDirection("asc");
            return;
        }
        if (episodeFilesSortDirection === "asc") {
            setEpisodeFilesSortDirection("desc");
            return;
        }
        setEpisodeFilesSortKey(null);
        setEpisodeFilesSortDirection("asc");
    };

    const episodeFilesSortIndicator = (key) => {
        if (episodeFilesSortKey !== key) return "";
        return episodeFilesSortDirection === "asc" ? " ↑" : " ↓";
    };

    const openEpisodeFolder = (item) => {
        if (!item || item.type !== "folder" || !form.id) return;
        setEpisodeFilesSearch("");
        loadEpisodeFiles(form.id, joinPath(episodeFilesPath, item.name));
    };

    const goToEpisodePath = (path) => {
        if (!form.id) return;
        setEpisodeFilesSearch("");
        loadEpisodeFiles(form.id, path || "/");
    };

    const goUpEpisodePath = () => {
        if (!form.id || episodeFilesPath === "/") return;
        const parent = episodeFilesPath.split("/").filter(Boolean).slice(0, -1);
        goToEpisodePath(parent.length ? `/${parent.join("/")}` : "/");
    };

    const handleEpisodeUploadFilesToPath = async (targetPath, files) => {
        if (!form.id || !episodeFilesCanUpload) return;
        if (episodeFilesUploading) {
            addToast(
                "A file upload is already in progress. Please wait before starting a second upload.",
                "warning",
                5000
            );
            return;
        }
        if (!files || files.length === 0) return;
        const uploadPath = String(targetPath || episodeFilesPath || "/");
        const totalFiles = files.length;
        const totalBytes = files.reduce(
            (sum, file) => sum + Number(file?.size || 0),
            0
        );
        let uploadedCount = 0;
        let uploadedBytes = 0;
        const uploadedWebhookFiles = [];
        setEpisodeFilesUploading(true);
        setEpisodeFilesUploadProgress(0);
        setEpisodeFilesError("");
        if (!episodeFilesUploadToastIdRef.current) {
            episodeFilesUploadToastIdRef.current = addToast(
                "Uploading files to episode storage… 0% You can keep using the portal.",
                "info",
                0
            );
        }
        try {
            for (const file of files) {
                const fileBytes = Number(file?.size || 0);
                const res = await uploadEpisodeFile(form.id, uploadPath, file, {
                    onProgress: ({ loaded }) => {
                        let percent = 0;
                        if (totalBytes > 0) {
                            const overallLoaded = Math.min(
                                totalBytes,
                                uploadedBytes + Math.max(0, Number(loaded || 0))
                            );
                            percent = Math.round((overallLoaded / totalBytes) * 100);
                        } else {
                            percent = Math.round((uploadedCount / totalFiles) * 100);
                        }
                        setEpisodeFilesUploadProgress(percent);
                        updateToast(
                            episodeFilesUploadToastIdRef.current,
                            `Uploading files to episode storage… ${percent}% You can keep using the portal.`
                        );
                    },
                });
                if (!res || !res.ok) {
                    throw new Error(
                        res?.message || `Failed to upload file: ${file?.name || "unknown"}`
                    );
                }
                const uploadedItem = res?.item && typeof res.item === "object" ? res.item : null;
                uploadedWebhookFiles.push({
                    item_id: uploadedItem?.id || null,
                    name: uploadedItem?.name || file?.name || null,
                    size:
                        uploadedItem?.size === null || uploadedItem?.size === undefined
                            ? Number(fileBytes || 0)
                            : Number(uploadedItem.size || 0),
                    mime_type: file?.type || "application/octet-stream",
                    path: uploadPath,
                    web_url: uploadedItem?.web_url || null,
                });
                uploadedCount += 1;
                uploadedBytes += fileBytes;
                const percent =
                    totalBytes > 0
                        ? Math.round((uploadedBytes / totalBytes) * 100)
                        : Math.round((uploadedCount / totalFiles) * 100);
                setEpisodeFilesUploadProgress(percent);
                updateToast(
                    episodeFilesUploadToastIdRef.current,
                    `Uploading files to episode storage… ${percent}% You can keep using the portal.`
                );
            }
            if (uploadedWebhookFiles.length) {
                const webhookRes = await completeEpisodeFileUploadBatch(form.id, {
                    path: uploadPath,
                    uploaded_files: uploadedWebhookFiles,
                });
                if (!webhookRes?.ok) {
                    console.warn(
                        "Episode upload-complete webhook trigger failed:",
                        webhookRes?.message || "Unknown error"
                    );
                }
            }
            await loadEpisodeFiles(form.id, episodeFilesPath);
            if (episodeFilesUploadToastIdRef.current) {
                removeToast(episodeFilesUploadToastIdRef.current);
                episodeFilesUploadToastIdRef.current = null;
            }
            addToast("File upload completed successfully.", "success", 5000);
        } catch (err) {
            setEpisodeFilesError(err.message || "Failed to upload file");
            if (episodeFilesUploadToastIdRef.current) {
                removeToast(episodeFilesUploadToastIdRef.current);
                episodeFilesUploadToastIdRef.current = null;
            }
            addToast(
                err.message || "File upload failed. Please try again.",
                "error",
                0
            );
        } finally {
            setEpisodeFilesUploading(false);
            setEpisodeFilesUploadProgress(0);
        }
    };

    const handleEpisodeUploadFiles = async (files) => {
        await handleEpisodeUploadFilesToPath(episodeFilesPath, files);
    };

    const handleEpisodeFilesDragEnter = (e) => {
        if (!episodeFilesCanUpload) return;
        if (!isExternalFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        episodeFilesDragDepthRef.current += 1;
        setEpisodeFilesDragOver(true);
    };

    const handleEpisodeFilesDragOver = (e) => {
        if (!episodeFilesCanUpload) return;
        if (!isExternalFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        if (!episodeFilesDragOver) setEpisodeFilesDragOver(true);
    };

    const handleEpisodeFilesDragLeave = (e) => {
        if (!episodeFilesCanUpload) return;
        if (!isExternalFileDrag(e) && !episodeFilesDragOver) return;
        e.preventDefault();
        e.stopPropagation();
        episodeFilesDragDepthRef.current = Math.max(
            0,
            episodeFilesDragDepthRef.current - 1
        );
        if (episodeFilesDragDepthRef.current === 0) {
            setEpisodeFilesDragOver(false);
            setEpisodeFilesFolderDropTargetId("");
        }
    };

    const handleEpisodeFilesDrop = async (e) => {
        if (!episodeFilesCanUpload) return;
        e.preventDefault();
        e.stopPropagation();
        episodeFilesDragDepthRef.current = 0;
        setEpisodeFilesDragOver(false);
        setEpisodeFilesFolderDropTargetId("");

        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (!droppedFiles.length) return;
        await handleEpisodeUploadFiles(droppedFiles);
    };

    const handleEpisodeFolderDrop = async (e, item) => {
        if (!episodeFilesCanUpload && !episodeFilesCanRenameDelete) return;
        if (!item || item.type !== "folder") return;
        e.preventDefault();
        e.stopPropagation();
        episodeFilesDragDepthRef.current = 0;
        setEpisodeFilesDragOver(false);
        setEpisodeFilesFolderDropTargetId("");
        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (droppedFiles.length) {
            const targetPath = joinPath(episodeFilesPath, item.name);
            await handleEpisodeUploadFilesToPath(targetPath, droppedFiles);
            return;
        }

        if (!form.id || !episodeFilesCanRenameDelete) return;
        const draggedId =
            String(e.dataTransfer?.getData("application/x-episode-item-id") || "").trim() ||
            String(episodeDraggedFileItemRef.current?.id || "").trim();
        const draggedName = String(episodeDraggedFileItemRef.current?.name || "").trim();
        if (!draggedId || !draggedName) return;
        if (draggedId === String(item.id || "")) return;

        setEpisodeFilesBusyAction(true);
        setEpisodeFilesError("");
        try {
            const res = await renameEpisodeFileItem(form.id, draggedId, draggedName, item.id);
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to move item");
            }
            await loadEpisodeFiles(form.id, episodeFilesPath);
        } catch (err) {
            setEpisodeFilesError(err.message || "Failed to move item");
        } finally {
            setEpisodeFilesBusyAction(false);
            episodeDraggedFileItemRef.current = null;
        }
    };

    const handleEpisodeItemDragStart = (e, item) => {
        if (!episodeFilesCanRenameDelete) return;
        if (!item?.id) return;
        episodeDraggedFileItemRef.current = item;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("application/x-episode-item-id", String(item.id));
            e.dataTransfer.setData("text/plain", String(item.id));
        }
    };

    const handleEpisodeItemDragEnd = () => {
        episodeDraggedFileItemRef.current = null;
        setEpisodeFilesFolderDropTargetId("");
    };

    useEffect(() => {
        const handleDocumentClick = () => {
            setActiveEpisodeFileMenuId(null);
        };
        document.addEventListener("click", handleDocumentClick);
        return () => document.removeEventListener("click", handleDocumentClick);
    }, []);

    const handleEpisodeFileDownload = async (item) => {
        if (!form.id || !item?.id || item.type !== "file") return;
        setEpisodeFilesError("");
        const urlRes = await getEpisodeFileStreamUrl(form.id, item.id);
        if (!urlRes || !urlRes.ok || !urlRes.stream_url) {
            setEpisodeFilesError(urlRes?.message || "Failed to download file");
            return;
        }

        const anchor = document.createElement("a");
        anchor.href = String(urlRes.stream_url);
        anchor.download = item.name || "download.bin";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const closeEpisodeFilePreview = () => {
        if (isBlobUrl(episodeFilePreviewUrl)) {
            window.URL.revokeObjectURL(episodeFilePreviewUrl);
        }
        setEpisodeFilePreviewUrl("");
        setEpisodeFilePreviewOpen(false);
        setEpisodeFilePreviewLoading(false);
        setEpisodeFilePreviewError("");
        setEpisodeFilePreviewName("");
        setEpisodeFilePreviewType("");
    };

    const handleEpisodeFilePreview = async (item) => {
        if (!form.id || !item?.id || item.type !== "file") return;

        const type = isAudioFile(item)
            ? "audio"
            : isVideoFile(item)
              ? "video"
            : isImageFile(item)
              ? "image"
            : "";
        if (!type) return;

        if (isBlobUrl(episodeFilePreviewUrl)) {
            window.URL.revokeObjectURL(episodeFilePreviewUrl);
        }

        setEpisodeFilePreviewOpen(true);
        setEpisodeFilePreviewLoading(true);
        setEpisodeFilePreviewError("");
        setEpisodeFilePreviewName(item.name || "Preview");
        setEpisodeFilePreviewType(type);
        setEpisodeFilePreviewUrl("");

        if (type === "video") {
            const urlRes = await getEpisodeFileStreamUrl(form.id, item.id);
            if (!urlRes || !urlRes.ok) {
                setEpisodeFilePreviewLoading(false);
                setEpisodeFilePreviewError(urlRes?.message || "Failed to load preview");
                return;
            }
            setEpisodeFilePreviewUrl(
                buildEpisodeFileStreamProxyUrl(form.id, item.id)
            );
            setEpisodeFilePreviewLoading(false);
            return;
        }

        if (item.download_url) {
            setEpisodeFilePreviewUrl(String(item.download_url));
            setEpisodeFilePreviewLoading(false);
            return;
        }

        const urlRes = await getEpisodeFileStreamUrl(form.id, item.id);
        if (!urlRes || !urlRes.ok || !urlRes.stream_url) {
            setEpisodeFilePreviewLoading(false);
            setEpisodeFilePreviewError(urlRes?.message || "Failed to load preview");
            return;
        }
        setEpisodeFilePreviewUrl(String(urlRes.stream_url));
        setEpisodeFilePreviewLoading(false);
    };

    useEffect(() => {
        return () => {
            if (isBlobUrl(episodeFilePreviewUrl)) {
                window.URL.revokeObjectURL(episodeFilePreviewUrl);
            }
        };
    }, [episodeFilePreviewUrl]);

    const handleUseEpisodeFileAsAudio = (item) => {
        if (!item || item.type !== "file") return;
        if (!isAudioFile(item)) return;
        if (!canUploadMedia || isFilesOnlyMode) return;
        setAudioFile(null);
        setForm((prev) => ({
            ...prev,
            selected_audio_file_item_id: String(item.id || ""),
            selected_audio_file_name: String(item.name || ""),
        }));
    };

    const handleUseEpisodeFileAsArtwork = (item) => {
        if (!item || item.type !== "file") return;
        if (!isImageFile(item)) return;
        if (!canUploadMedia || isFilesOnlyMode) return;
        setArtworkFile(null);
        setForm((prev) => ({
            ...prev,
            selected_artwork_file_item_id: String(item.id || ""),
            selected_artwork_file_name: String(item.name || ""),
        }));
    };

    const handleEpisodeCreateFolder = async () => {
        if (!episodeFilesCanCreateFolder) return;
        if (!form.id) return;
        const name = window.prompt("Folder name");
        if (!name || !name.trim()) return;

        setEpisodeFilesBusyAction(true);
        setEpisodeFilesError("");
        try {
            const res = await createEpisodeFolder(form.id, episodeFilesPath, name.trim());
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to create folder");
            }
            await loadEpisodeFiles(form.id, episodeFilesPath);
        } catch (err) {
            setEpisodeFilesError(err.message || "Failed to create folder");
        } finally {
            setEpisodeFilesBusyAction(false);
        }
    };

    const handleEpisodeDeleteItem = async (item) => {
        if (!episodeFilesCanRenameDelete) return;
        if (!form.id || !item?.id) return;
        const ok = window.confirm(
            `Delete ${item.type} "${item.name}"? This cannot be undone.`
        );
        if (!ok) return;

        setEpisodeFilesBusyAction(true);
        setEpisodeFilesError("");
        try {
            const res = await deleteEpisodeFileItem(form.id, item.id);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to delete item");
            }
            await loadEpisodeFiles(form.id, episodeFilesPath);
        } catch (err) {
            setEpisodeFilesError(err.message || "Failed to delete item");
        } finally {
            setEpisodeFilesBusyAction(false);
        }
    };

    const handleEpisodeRenameItem = async (item) => {
        if (!episodeFilesCanRenameDelete) return;
        if (!form.id || !item?.id) return;
        const isFile = item?.type === "file";
        const { baseName, extensionWithDot } = splitFileNameAndExtension(item?.name);
        const hasLockedExtension = isFile && Boolean(extensionWithDot);
        const newName = window.prompt(
            hasLockedExtension ? `New name (without ${extensionWithDot})` : "New name",
            hasLockedExtension ? baseName : item.name
        );
        if (newName === null) return;
        const trimmedName = newName.trim();
        if (!trimmedName) return;
        const trimmedWithoutMatchingExt =
            hasLockedExtension &&
            trimmedName.toLowerCase().endsWith(extensionWithDot.toLowerCase())
                ? trimmedName.slice(0, -extensionWithDot.length).trim()
                : trimmedName;
        if (!trimmedWithoutMatchingExt) return;
        const finalName = hasLockedExtension
            ? `${trimmedWithoutMatchingExt}${extensionWithDot}`
            : trimmedWithoutMatchingExt;

        setEpisodeFilesBusyAction(true);
        setEpisodeFilesError("");
        try {
            const res = await renameEpisodeFileItem(form.id, item.id, finalName);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to rename item");
            }
            await loadEpisodeFiles(form.id, episodeFilesPath);
        } catch (err) {
            setEpisodeFilesError(err.message || "Failed to rename item");
        } finally {
            setEpisodeFilesBusyAction(false);
        }
    };

    const renderEpisodeFileRowActions = (item) => {
        const hasAnyAction =
            item.type === "file" ||
            (item.type === "file" && canUploadMedia && !isFilesOnlyMode && isAudioFile(item)) ||
            (item.type === "file" &&
                canUploadMedia &&
                !isFilesOnlyMode &&
                isImageFile(item)) ||
            episodeFilesCanRenameDelete;
        if (!hasAnyAction) return null;
        const isOpen = activeEpisodeFileMenuId === item.id;

        return (
            <>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            width: 1,
                            height: 18,
                            background: "rgba(148, 163, 184, 0.35)",
                            display: "inline-block",
                        }}
                    />
                    <button
                        type="button"
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "rgba(148, 163, 184, 0.9)",
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1,
                            width: 16,
                            height: 16,
                            borderRadius: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            opacity: 0.85,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveEpisodeFileMenuId((current) =>
                                current === item.id ? null : item.id
                            );
                        }}
                        aria-label="Open actions menu"
                    >
                        ⋯
                    </button>
                </div>
                {isOpen && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: "absolute",
                        right: 0,
                        bottom: "calc(100% + 4px)",
                        zIndex: 20,
                        minWidth: 150,
                        maxHeight: 240,
                        overflowY: "auto",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        background: "#0f172a",
                        padding: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                    }}
                >
                    {item.type === "file" &&
                        (isAudioFile(item) || isVideoFile(item) || isImageFile(item)) && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveEpisodeFileMenuId(null);
                                handleEpisodeFilePreview(item);
                            }}
                            disabled={episodeFilesBusyAction}
                        >
                            Preview
                        </button>
                    )}
                    {item.type === "file" && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveEpisodeFileMenuId(null);
                                handleEpisodeFileDownload(item);
                            }}
                            disabled={episodeFilesBusyAction}
                        >
                            Download
                        </button>
                    )}
                    {item.type === "file" &&
                        isAdminUser &&
                        (isAudioFile(item) || isVideoFile(item)) && (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveEpisodeFileMenuId(null);
                                    handleTranscribeEpisodeFile(item);
                                }}
                                disabled={
                                    episodeFilesBusyAction ||
                                    transcribingAudio ||
                                    processingAudio
                                }
                            >
                                {transcribingAudio ? "Transcribing…" : "Transcribe media"}
                            </button>
                        )}
                    {item.type === "file" &&
                        isAdminUser &&
                        isAudioFile(item) && (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveEpisodeFileMenuId(null);
                                    handleOpenProcessAudioPresetModal(item);
                                }}
                                disabled={
                                    episodeFilesBusyAction ||
                                    processingAudio ||
                                    transcribingAudio ||
                                    generatingShowNotes
                                }
                            >
                                {processingAudio ? "Processing…" : "Process Audio"}
                            </button>
                        )}
                    {item.type === "file" &&
                        isAdminUser &&
                        isAudioFile(item) && (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveEpisodeFileMenuId(null);
                                    handleTranscribeAndGenerateShowNotesFromEpisodeFile(
                                        item
                                    );
                                }}
                                disabled={
                                    episodeFilesBusyAction ||
                                    transcribingAudio ||
                                    processingAudio ||
                                    generatingShowNotes
                                }
                            >
                                {transcribingAudio
                                    ? "Transcribing…"
                                    : processingAudio
                                      ? "Processing…"
                                    : generatingShowNotes
                                      ? "Generating Show Notes…"
                                      : "Transcribe + show notes"}
                            </button>
                        )}
                    {item.type === "file" &&
                        canUploadMedia &&
                        !isFilesOnlyMode &&
                        isAudioFile(item) && (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveEpisodeFileMenuId(null);
                                    handleUseEpisodeFileAsAudio(item);
                                }}
                                disabled={episodeFilesBusyAction}
                            >
                                {String(form.selected_audio_file_item_id || "") ===
                                String(item.id)
                                    ? "Audio selected"
                                    : "Use as audio"}
                            </button>
                        )}
                    {item.type === "file" &&
                        canUploadMedia &&
                        !isFilesOnlyMode &&
                        isImageFile(item) && (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveEpisodeFileMenuId(null);
                                    handleUseEpisodeFileAsArtwork(item);
                                }}
                                disabled={episodeFilesBusyAction}
                            >
                                {String(form.selected_artwork_file_item_id || "") ===
                                String(item.id)
                                    ? "Artwork selected"
                                    : "Use as artwork"}
                            </button>
                        )}
                    {episodeFilesCanRenameDelete && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveEpisodeFileMenuId(null);
                                handleEpisodeRenameItem(item);
                            }}
                            disabled={episodeFilesBusyAction}
                        >
                            Rename
                        </button>
                    )}
                    {episodeFilesCanRenameDelete && (
                        <button
                            type="button"
                            className="btn btn--danger btn--tiny"
                            onClick={() => {
                                setActiveEpisodeFileMenuId(null);
                                handleEpisodeDeleteItem(item);
                            }}
                            disabled={episodeFilesBusyAction}
                        >
                            Delete
                        </button>
                    )}
                </div>
                )}
            </>
        );
    };

    const handleAddEpisodeExternalLink = async () => {
        if (!form.id || !episodeFilesCanUpload) return;

        const title = String(episodeExternalLinkTitle || "").trim();
        const rawUrl = String(episodeExternalLinkUrl || "").trim();
        const url = normalizeExternalLinkUrl(episodeExternalLinkUrl);
        const notes = String(episodeExternalLinkNotes || "").trim();

        if (!title) {
            setEpisodeExternalLinksError("Link title is required");
            return;
        }
        if (!rawUrl) {
            setEpisodeExternalLinksError("Link URL is required");
            return;
        }
        if (!url) {
            setEpisodeExternalLinksError(
                "Please enter a valid http:// or https:// URL"
            );
            return;
        }

        setEpisodeExternalLinksBusy(true);
        setEpisodeExternalLinksError("");
        try {
            const res = await createEpisodeExternalLink(form.id, { title, url, notes });
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to add external link");
            }
            setEpisodeExternalLinkTitle("");
            setEpisodeExternalLinkUrl("");
            setEpisodeExternalLinkNotes("");
            await loadEpisodeExternalLinks(form.id);
        } catch (err) {
            setEpisodeExternalLinksError(err.message || "Failed to add external link");
        } finally {
            setEpisodeExternalLinksBusy(false);
        }
    };

    const handleEditEpisodeExternalLink = async (link) => {
        if (!form.id || !episodeFilesCanRenameDelete || !link?.id) return;
        const nextTitle = window.prompt("Link title", link.title || "");
        if (nextTitle === null) return;
        const title = String(nextTitle || "").trim();
        if (!title) return;

        const nextUrl = window.prompt("Link URL", link.url || "");
        if (nextUrl === null) return;
        const rawUrl = String(nextUrl || "").trim();
        const url = normalizeExternalLinkUrl(nextUrl);
        if (!rawUrl) {
            setEpisodeExternalLinksError("Link URL is required");
            return;
        }
        if (!url) {
            setEpisodeExternalLinksError(
                "Please enter a valid http:// or https:// URL"
            );
            return;
        }

        const nextNotes = window.prompt("Notes (optional)", link.notes || "");
        if (nextNotes === null) return;
        const notes = String(nextNotes || "").trim();

        setEpisodeExternalLinksBusy(true);
        setEpisodeExternalLinksError("");
        try {
            const res = await updateEpisodeExternalLink(form.id, link.id, {
                title,
                url,
                notes,
            });
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to update external link");
            }
            await loadEpisodeExternalLinks(form.id);
        } catch (err) {
            setEpisodeExternalLinksError(
                err.message || "Failed to update external link"
            );
        } finally {
            setEpisodeExternalLinksBusy(false);
        }
    };

    const handleDeleteEpisodeExternalLink = async (link) => {
        if (!form.id || !episodeFilesCanRenameDelete || !link?.id) return;
        const ok = window.confirm(
            `Delete external link "${link.title || link.url || "link"}"?`
        );
        if (!ok) return;

        setEpisodeExternalLinksBusy(true);
        setEpisodeExternalLinksError("");
        try {
            const res = await deleteEpisodeExternalLink(form.id, link.id);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to delete external link");
            }
            await loadEpisodeExternalLinks(form.id);
        } catch (err) {
            setEpisodeExternalLinksError(
                err.message || "Failed to delete external link"
            );
        } finally {
            setEpisodeExternalLinksBusy(false);
        }
    };

    // Delete handler
    const handleDelete = async () => {
        if (!form.id) return;
        const ok = window.confirm(
            "Are you sure you want to delete this episode? This cannot be undone."
        );
        if (!ok) return;

        try {
            const res = await deleteEpisode(form.id);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to delete episode");
            }

            const listRes = await getEpisodes();
            if (listRes && listRes.ok) {
                setEpisodes(listRes.episodes || []);
            }

            forceCloseModal();
        } catch (err) {
            console.error("Delete episode error:", err);
            setModalError(err.message || "Failed to delete episode");
        }
    };

    // Notifications (webhook flow)
    const openNotifyModal = () => {
        resetNotifyState();
        setIsNotifyOpen(true);
    };

    const closeNotifyModal = () => {
        setIsNotifyOpen(false);
        resetNotifyState();
    };

    const handleSendNotification = async (e) => {
        e.preventDefault();
        setNotifyError("");
        setNotifySuccess("");

        if (!form.id) {
            setNotifyError("No episode selected.");
            return;
        }

        const msg = (notifyMessage || "").trim();
        if (!msg) {
            setNotifyError("Please enter a message.");
            return;
        }

        try {
            setNotifying(true);
            const res = await notifyEpisode(form.id, msg);

            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to send notification");
            }

            setNotifySuccess("Notification sent");
            setNotifying(false);

            setTimeout(() => closeNotifyModal(), 600);
        } catch (err) {
            console.error("Notify error:", err);
            setNotifyError(err.message || "Failed to send notification");
            setNotifying(false);
        }
    };

    // ---------- CHANGE LOG MODAL ----------

    const openChangeLogModal = async () => {
        if (!form.id) return;
        setChangeLogsError("");
        setChangeLogs([]);
        setIsChangeLogOpen(true);
        setChangeLogsLoading(true);

        try {
            const res = await getEpisodeChangeLogs(form.id);
            if (!res || !res.ok) {
                setChangeLogsError(res?.message || "Failed to load change history");
                setChangeLogs([]);
            } else {
                const rows = res.logs || res.change_logs || [];
                setChangeLogs(rows);
            }
        } catch (err) {
            console.error("Change logs load error:", err);
            setChangeLogsError("Failed to load change history");
            setChangeLogs([]);
        } finally {
            setChangeLogsLoading(false);
        }
    };

    const closeChangeLogModal = () => {
        setIsChangeLogOpen(false);
        resetChangeLogState();
    };

    // ---------- SUGGESTED CHANGES MODAL ----------

    const openSuggestModal = () => {
        if (!suggestedChanges) return;

        const hasTitle =
            suggestedChanges.title &&
            typeof suggestedChanges.title === "string" &&
            suggestedChanges.title.trim() !== "";

        const hasNotes =
            suggestedChanges.show_notes_captivate &&
            typeof suggestedChanges.show_notes_captivate === "string" &&
            suggestedChanges.show_notes_captivate.trim() !== "";

        setApplyTitleSuggestion(!!hasTitle);
        setApplyShowNotesSuggestion(!!hasNotes);
        setIsSuggestModalOpen(true);
    };

    const closeSuggestModal = () => {
        setIsSuggestModalOpen(false);
    };

    const handleAcceptSuggestedChanges = () => {
        if (!suggestedChanges) {
            setIsSuggestModalOpen(false);
            return;
        }

        setForm((prev) => {
            const updated = { ...prev };

            if (
                applyTitleSuggestion &&
                suggestedChanges.title &&
                typeof suggestedChanges.title === "string"
            ) {
                updated.title = suggestedChanges.title;
            }

            if (
                applyShowNotesSuggestion &&
                suggestedChanges.show_notes_captivate &&
                typeof suggestedChanges.show_notes_captivate === "string"
            ) {
                updated.show_notes_captivate =
                    suggestedChanges.show_notes_captivate;
            }

            return updated;
        });

        // Mark to clear suggestions server-side on save
        setClearSuggestedOnSave(true);
        setSuggestedChanges(null);
        setHasClientSuggestedChanges(false);
        setIsSuggestModalOpen(false);
    };

    const handleDiscardSuggestedChanges = () => {
        // Don't touch current form; just mark suggestions as reviewed/cleared
        setClearSuggestedOnSave(true);
        setSuggestedChanges(null);
        setHasClientSuggestedChanges(false);
        setIsSuggestModalOpen(false);
    };

    // ---------- SHOW NOTES SCROLL SYNC ----------

    const handleShowNotesScroll = (source) => {
        const currentEl = currentNotesScrollRef.current;
        const suggestedEl = suggestedNotesScrollRef.current;

        if (!currentEl || !suggestedEl) return;

        // Prevent scroll event ping-pong
        if (showNotesSyncingRef.current) return;

        showNotesSyncingRef.current = true;

        try {
            if (source === "current") {
                const maxCurrent =
                    currentEl.scrollHeight - currentEl.clientHeight || 1;
                const ratio = currentEl.scrollTop / maxCurrent;

                const maxSuggested =
                    suggestedEl.scrollHeight - suggestedEl.clientHeight || 1;
                suggestedEl.scrollTop = ratio * maxSuggested;
            } else if (source === "suggested") {
                const maxSuggested =
                    suggestedEl.scrollHeight - suggestedEl.clientHeight || 1;
                const ratio = suggestedEl.scrollTop / maxSuggested;

                const maxCurrent =
                    currentEl.scrollHeight - currentEl.clientHeight || 1;
                currentEl.scrollTop = ratio * maxCurrent;
            }
        } finally {
            window.requestAnimationFrame(() => {
                showNotesSyncingRef.current = false;
            });
        }
    };

    const normalizeStatusValue = (value) =>
        String(value || "").toLowerCase().replace(/\s+/g, "").trim();
    const isPendingApprovalStatus = (value) =>
        normalizeStatusValue(value) === "pendingapproval";

    // Save episode (create or update) + handle uploads
    const executeSave = async ({ reviewSendNotesOverride } = {}) => {
        if (isFilesOnlyMode) {
            forceCloseModal();
            return;
        }

        const isNew = !form.id;
        const isRequestedCreateFlow = isNew && isClientUser && createAsRequested;
        if (!canSaveEpisode && !isRequestedCreateFlow) {
            setModalError("You do not have permission to edit this episode.");
            return;
        }

        setSaving(true);
        setModalError("");
        setIsUploading(false);
        setUploadProgress(0);

        const pendingAudioFile = audioFile;
        const pendingArtworkFile = artworkFile;
        const episodeId = form.id;

        const payload = {};
        if (isRequestedCreateFlow) {
            payload.podcast_id = form.podcast_id || null;
            payload.title = form.title || "";
            payload.status = "Requested";
        } else if (canEditEpisodeMetadata) {
            payload.podcast_id = form.podcast_id || null;
            payload.title = form.title || "";
            payload.episode_number = form.episode_number || null;
            payload.season_number = form.season_number || null;
            payload.publication_date = form.publication_date || null;
            payload.publication_time = form.publication_time || null;
            payload.status = form.status || null;
            const isPendingApproval = isPendingApprovalStatus(form.status);
            const reviewSendNotesInput =
                reviewSendNotesOverride !== undefined
                    ? reviewSendNotesOverride
                    : form.review_send_notes;
            payload.review_send_notes = isPendingApproval
                ? String(reviewSendNotesInput || "").trim() || null
                : null;
            payload.captivate_episode_id = form.captivate_episode_id || null;
            payload.episode_type = form.episode_type || null;
            payload.explicit = form.explicit ? 1 : 0;
            if (isAdminUser) {
                payload.sharepoint_folder_path =
                    String(form.sharepoint_folder_path || "").trim() || null;
            }
        }
        if (!isRequestedCreateFlow && canEditEpisodeNotes) {
            payload.notes = form.notes || null;
        }
        if (!isRequestedCreateFlow && canEditShowNotes) {
            payload.show_notes_captivate = form.show_notes_captivate || null;
        }

        // If we've accepted/discarded suggested changes, clear them on save
        if (clearSuggestedOnSave && canEditEpisodeMetadata) {
            payload.client_suggested_changes = null;
        }

        const initialEpisodeState = initialFormRef.current || emptyForm;
        const selectedAudioItemId = String(form.selected_audio_file_item_id || "").trim();
        const selectedArtworkItemId = String(
            form.selected_artwork_file_item_id || ""
        ).trim();
        const selectedAudioChanged =
            !!selectedAudioItemId &&
            selectedAudioItemId !==
                String(initialEpisodeState.selected_audio_file_item_id || "").trim();
        const selectedArtworkChanged =
            !!selectedArtworkItemId &&
            selectedArtworkItemId !==
                String(initialEpisodeState.selected_artwork_file_item_id || "").trim();

        const hasMediaUploads =
            !!pendingAudioFile ||
            !!pendingArtworkFile ||
            selectedAudioChanged ||
            selectedArtworkChanged;
        let uploadToastId = null;

        if (
            !isNew &&
            hasMediaUploads &&
            globalMediaUploading &&
            globalMediaUploadEpisodeId !== episodeId
        ) {
            addToast(
                "Another media upload is currently in progress. Please wait for it to finish before uploading more media.",
                "warning",
                7000
            );
            setSaving(false);
            return;
        }

        try {
            if (isNew) {
                if (canCreateLocalEpisode && createWithoutCaptivateSync) {
                    payload.create_local_episode = true;
                }
                const res = await createEpisode(payload);
                if (!res || !res.ok) {
                    throw new Error(res?.message || "Failed to create episode");
                }
                const createdEpisodeId = Number(res.id || 0);
                const isPlannedCreate =
                    normalizeStatusValue(payload.status || "") === "planned";
                const isLocalCreate = Boolean(payload.create_local_episode);
                if (isAdminUser && isPlannedCreate && !isLocalCreate && createdEpisodeId) {
                    try {
                        const folderRes = await createEpisodeSharePointFolder(
                            createdEpisodeId
                        );
                        if (
                            !folderRes?.ok &&
                            !String(folderRes?.message || "")
                                .toLowerCase()
                                .includes("already has a sharepoint folder")
                        ) {
                            throw new Error(
                                folderRes?.message ||
                                    "Failed to create SharePoint folder for this episode"
                            );
                        }
                    } catch (folderErr) {
                        addToast(
                            folderErr?.message ||
                                "Failed to create SharePoint folder for this episode",
                            "warning",
                            6000
                        );
                    }
                }
            } else {
                let progressSteps = 1;
                if (selectedAudioChanged) progressSteps += 1;
                if (selectedArtworkChanged) progressSteps += 1;
                if (pendingAudioFile) progressSteps += 1;
                if (pendingArtworkFile) progressSteps += 1;

                let currentStep = 0;
                const stepToPercent = () =>
                    Math.round((currentStep / progressSteps) * 100);
                const inFlightStepPercent = (lead = 0.25) => {
                    const clampedLead = Math.max(0.05, Math.min(0.9, Number(lead || 0.25)));
                    return Math.round(((currentStep + clampedLead) / progressSteps) * 100);
                };
                const setMediaProgress = (percent) => {
                    setIsUploading(true);
                    setUploadProgress(percent);
                    if (uploadToastId) {
                        updateToast(
                            uploadToastId,
                            `Applying media changes… ${percent}% You can keep using the portal.`
                        );
                    }
                };

                if (hasMediaUploads) {
                    setGlobalMediaUploading(true);
                    setGlobalMediaUploadEpisodeId(episodeId);

                    uploadToastId = addToast(
                        "Applying media changes… 0% You can keep using the portal.",
                        "info",
                        0
                    );

                    forceCloseModal();
                }

                if (selectedAudioChanged) {
                    setMediaProgress(inFlightStepPercent(0.25));
                    const selectedAudioRes = await useEpisodeFileAsAudio(
                        episodeId,
                        selectedAudioItemId
                    );
                    currentStep += 1;
                    setMediaProgress(stepToPercent());
                    if (!selectedAudioRes || !selectedAudioRes.ok) {
                        throw new Error(
                            selectedAudioRes?.message ||
                                "Failed to use selected episode file as audio"
                        );
                    }
                }

                if (selectedArtworkChanged) {
                    setMediaProgress(inFlightStepPercent(0.25));
                    const selectedArtworkRes = await useEpisodeFileAsArtwork(
                        episodeId,
                        selectedArtworkItemId
                    );
                    currentStep += 1;
                    setMediaProgress(stepToPercent());
                    if (!selectedArtworkRes || !selectedArtworkRes.ok) {
                        throw new Error(
                            selectedArtworkRes?.message ||
                                "Failed to use selected episode file as artwork"
                        );
                    }
                }

                if (pendingAudioFile) {
                    setMediaProgress(stepToPercent());
                    const stepStart = currentStep / progressSteps;
                    const stepSize = 1 / progressSteps;
                    const audioRes = await uploadEpisodeMedia(
                        episodeId,
                        pendingAudioFile,
                        {
                            onProgress: ({ ratio }) => {
                                const safeRatio = Math.max(
                                    0,
                                    Math.min(1, Number(ratio || 0))
                                );
                                setMediaProgress(
                                    Math.round((stepStart + stepSize * safeRatio) * 100)
                                );
                            },
                        }
                    );
                    currentStep += 1;
                    setMediaProgress(stepToPercent());

                    if (!audioRes || !audioRes.ok) {
                        throw new Error(
                            audioRes?.message ||
                            "Failed to upload audio to Captivate"
                        );
                    }
                }

                if (pendingArtworkFile) {
                    setMediaProgress(stepToPercent());
                    const stepStart = currentStep / progressSteps;
                    const stepSize = 1 / progressSteps;
                    const artRes = await uploadEpisodeArtwork(
                        episodeId,
                        pendingArtworkFile,
                        {
                            onProgress: ({ ratio }) => {
                                const safeRatio = Math.max(
                                    0,
                                    Math.min(1, Number(ratio || 0))
                                );
                                setMediaProgress(
                                    Math.round((stepStart + stepSize * safeRatio) * 100)
                                );
                            },
                        }
                    );
                    currentStep += 1;
                    setMediaProgress(stepToPercent());

                    if (!artRes || !artRes.ok) {
                        throw new Error(
                            artRes?.message ||
                            "Failed to upload artwork to Captivate"
                        );
                    }
                }

                setMediaProgress(stepToPercent());

                const res = await updateEpisode(episodeId, payload);
                currentStep += 1;
                setMediaProgress(stepToPercent());

                if (!res || !res.ok) {
                    throw new Error(res?.message || "Failed to update episode");
                }

                initialFormRef.current = {
                    ...initialEpisodeState,
                    ...payload,
                    selected_audio_file_item_id: selectedAudioItemId,
                    selected_audio_file_name: form.selected_audio_file_name || "",
                    selected_artwork_file_item_id: selectedArtworkItemId,
                    selected_artwork_file_name: form.selected_artwork_file_name || "",
                };
            }

            const listRes = await getEpisodes();
            if (listRes && listRes.ok) {
                setEpisodes(listRes.episodes || []);
            }

            setUploadProgress(100);
            if (uploadToastId) {
                updateToast(
                    uploadToastId,
                    "Applying media changes… 100% You can keep using the portal."
                );
            }

            if (uploadToastId) {
                removeToast(uploadToastId);
                addToast("Media upload completed successfully.", "success", 5000);
            } else {
                forceCloseModal();
            }

            // Reset suggestion-clear flag after successful save
            setClearSuggestedOnSave(false);
        } catch (err) {
            console.error("Save episode error:", err);
            setModalError(err.message || "Failed to save episode");

            if (uploadToastId) {
                removeToast(uploadToastId);
                addToast(
                    err.message || "Media upload failed. Please try again.",
                    "error",
                    0
                );
            }
        } finally {
            setSaving(false);
            setIsUploading(false);

            if (hasMediaUploads) {
                setGlobalMediaUploading(false);
                setGlobalMediaUploadEpisodeId(null);
            }
        }
    };

    const closeReviewNotesPrompt = () => {
        if (saving) return;
        setIsReviewNotesPromptOpen(false);
    };

    const handleReviewNotesPromptDone = async () => {
        setForm((prev) => ({ ...prev, review_send_notes: reviewNotesDraft }));
        setIsReviewNotesPromptOpen(false);
        await executeSave({ reviewSendNotesOverride: reviewNotesDraft });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const previousStatus = initialFormRef.current?.status;
        const nextStatus = form.status;
        const shouldOpenReviewNotesPrompt =
            !saving &&
            canEditEpisodeMetadata &&
            !isPendingApprovalStatus(previousStatus) &&
            isPendingApprovalStatus(nextStatus);

        if (shouldOpenReviewNotesPrompt) {
            setReviewNotesDraft(String(form.review_send_notes || ""));
            setIsReviewNotesPromptOpen(true);
            return;
        }

        await executeSave();
    };

    const isEditing = !!form.id;
    const isAdminUser = currentUserRole === "admin";
    const hasPendingMedia = !!audioFile || !!artworkFile;
    const isClientUser = currentUserRole === "client";
    const isRequestedCreateFlow = !isEditing && isClientUser && createAsRequested;
    const canEditEpisodeMetadata = !!episodeCapabilities.edit_episode_metadata;
    const canEditEpisodeNotes =
        isAdminUser || !!episodeCapabilities.episode_files_upload;
    const canEditShowNotes = !!episodeCapabilities.edit_show_notes;
    const canUploadMedia = !!episodeCapabilities.upload_media;
    const canSendNotifications = !!episodeCapabilities.send_notifications;
    const canViewChangeHistory = !!episodeCapabilities.view_change_history;
    const canSaveEpisode =
        canEditEpisodeMetadata || canEditShowNotes || canEditEpisodeNotes;
    const isEpisodeReadOnly = !canEditEpisodeMetadata;
    const isShowNotesReadOnly = !canEditShowNotes;
    const showNotesStatus = String(form.status || "").toLowerCase().trim();
    const canClientViewReadOnlyShowNotes = [
        "pending approval",
        "approved",
        "scheduled",
        "published",
    ].includes(showNotesStatus);
    const canClientViewReadOnlyMetadata = canClientViewReadOnlyShowNotes;
    const shouldShowExtendedMetadataFields =
        !isClientUser || canEditEpisodeMetadata || canClientViewReadOnlyMetadata;
    const shouldShowShowNotesSection =
        !isClientUser || canEditShowNotes || canClientViewReadOnlyShowNotes;
    const shouldShowEpisodeAudioSection =
        (isClientUser || isAdminUser) &&
        !isFilesOnlyMode &&
        !isRequestedCreateFlow &&
        ["scheduled", "published"].includes(showNotesStatus);
    const canViewEpisodeFilesSection =
        isFilesOnlyMode || isAdminUser || !!episodeCapabilities.view_file_browser;
    const canAccessEpisodeFiles =
        !!String(form.sharepoint_folder_path || "").trim() &&
        canViewEpisodeFilesSection;
    const selectedEpisodePodcast = podcasts.find(
        (p) => String(p.id) === String(form.podcast_id)
    );
    const isDistributionEnabledForForm = isDistributionEnabled(
        form.podcast_distribution ?? selectedEpisodePodcast?.distribution
    );
    const shouldHidePublicationMetaForClient =
        isClientUser && !isDistributionEnabledForForm;
    const selectedEpisodePodcastHasSharePointFolder = !!String(
        selectedEpisodePodcast?.sharepoint_folder_name || ""
    ).trim();
    const sharePointEpisodeFolderName = getPathLeafName(
        form.sharepoint_folder_path
    );
    const showSharePointFolderEditor =
        isAdminUser && selectedEpisodePodcastHasSharePointFolder;
    const canShowCaptivateMediaUploads =
        !isClientUser && canUploadMedia && !!form.captivate_episode_id;
    const showMediaFieldset = !isClientUser && (
        canShowCaptivateMediaUploads || showSharePointFolderEditor
    );
    const hasRightColumnContent =
        !isFilesOnlyMode &&
        !isClientUser &&
        !isRequestedCreateFlow &&
        (showMediaFieldset || isEditing || !!form.client_reviewed_at);
    const hasClientReviewAttachments =
        Array.isArray(form.client_review_attachments) &&
        form.client_review_attachments.length > 0;
    const canCreateLocalEpisode =
        isAdminUser &&
        !isFilesOnlyMode &&
        !isEditing &&
        !isRequestedCreateFlow &&
        selectedFormPodcastHasCaptivateShow;
    const hasClientReviewData =
        !!form.client_reviewed_at ||
        !!String(form.client_review_comments || "").trim() ||
        hasClientReviewAttachments ||
        hasClientSuggestedChanges;

    const saveDisabled =
        (!canSaveEpisode && !isRequestedCreateFlow) ||
        saving ||
        (hasPendingMedia &&
            globalMediaUploading &&
            globalMediaUploadEpisodeId !== form.id);

    const mediaUploadPercent = Math.max(
        0,
        Math.min(100, Math.round(Number(uploadProgress || 0)))
    );
    let saveLabel = isEditing ? "Save" : "Create";
    if (isRequestedCreateFlow && !saving) {
        saveLabel = "Request Episode";
    }
    if (saving && hasPendingMedia) {
        saveLabel = `Saving & uploading… ${mediaUploadPercent}%`;
    } else if (saving) {
        if (isRequestedCreateFlow) {
            saveLabel = "Requesting…";
        } else {
            saveLabel = isEditing ? "Saving…" : "Creating…";
        }
    } else if (
        hasPendingMedia &&
        globalMediaUploading &&
        globalMediaUploadEpisodeId !== form.id
    ) {
        saveLabel = "Waiting for other upload…";
    }

    useEffect(() => {
        if (canCreateLocalEpisode) return;
        setCreateWithoutCaptivateSync(false);
    }, [canCreateLocalEpisode]);

    // ---------- KEYBOARD SHORTCUTS ----------
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (
                !isModalOpen &&
                !isSelectPodcastOpen &&
                !isNotifyOpen &&
                !isChangeLogOpen &&
                !isSuggestModalOpen &&
                !isEpisodeNotesQuickOpen &&
                !isReviewNotesPromptOpen &&
                !episodeFilePreviewOpen &&
                !isShareOpen &&
                !isTranscriptPreviewOpen &&
                !isMultitrackModalOpen &&
                !isProcessAudioPresetModalOpen
            ) {
                return;
            }

            const active = document.activeElement;
            const tagName = active?.tagName;
            const isInputLike =
                tagName === "INPUT" ||
                tagName === "TEXTAREA" ||
                tagName === "SELECT" ||
                active?.isContentEditable;

            // ESC: close modals (stack: suggested → change-log → notify → main → select-podcast)
            if (e.key === "Escape") {
                e.preventDefault();
                if (episodeFilePreviewOpen) {
                    closeEpisodeFilePreview();
                    return;
                }
                if (isReviewNotesPromptOpen) {
                    closeReviewNotesPrompt();
                    return;
                }
                if (isEpisodeNotesQuickOpen) {
                    closeEpisodeNotesQuickModal();
                    return;
                }
                if (isSuggestModalOpen) {
                    closeSuggestModal();
                    return;
                }
                if (isChangeLogOpen) {
                    closeChangeLogModal();
                    return;
                }
                if (isNotifyOpen) {
                    closeNotifyModal();
                    return;
                }
                if (isShareOpen) {
                    closeShareModal();
                    return;
                }
                if (isTranscriptPreviewOpen) {
                    closeTranscriptPreviewModal();
                    return;
                }
                if (isProcessAudioPresetModalOpen) {
                    closeProcessAudioPresetModal();
                    return;
                }
                if (isMultitrackModalOpen) {
                    closeMultitrackModal();
                    return;
                }
                if (isModalOpen) {
                    closeModal();
                    return;
                }
                if (isSelectPodcastOpen) {
                    handleCancelSelectPodcast();
                    return;
                }
            }

            // Cmd/Ctrl + S → save episode (main modal)
            if (
                (e.key === "s" || e.key === "S") &&
                (e.metaKey || e.ctrlKey) &&
                isModalOpen &&
                !isNotifyOpen &&
                !isChangeLogOpen &&
                !isSuggestModalOpen &&
                !isEpisodeNotesQuickOpen &&
                !isReviewNotesPromptOpen &&
                !isShareOpen &&
                !isTranscriptPreviewOpen
            ) {
                e.preventDefault();

                const localHasPendingMedia = !!audioFile || !!artworkFile;
                const blockedByGlobalUpload =
                    localHasPendingMedia &&
                    globalMediaUploading &&
                    globalMediaUploadEpisodeId !== form.id;

                if (!saving && !blockedByGlobalUpload && episodeFormRef.current) {
                    episodeFormRef.current.requestSubmit();
                }
                return;
            }

            // Cmd/Ctrl + S → save quick episode notes modal
            if (
                (e.key === "s" || e.key === "S") &&
                (e.metaKey || e.ctrlKey) &&
                isEpisodeNotesQuickOpen
            ) {
                e.preventDefault();
                if (!episodeNotesQuickSaving && canEditEpisodeNotes) {
                    saveEpisodeNotesQuickModal();
                }
                return;
            }

            // Cmd/Ctrl + Enter → send notification
            if (
                (e.key === "Enter" || e.key === "NumpadEnter") &&
                (e.metaKey || e.ctrlKey) &&
                isNotifyOpen
            ) {
                e.preventDefault();
                if (!notifying && notifyFormRef.current) {
                    notifyFormRef.current.requestSubmit();
                }
                return;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [
        isModalOpen,
        isSelectPodcastOpen,
        isNotifyOpen,
        isChangeLogOpen,
        isSuggestModalOpen,
        isEpisodeNotesQuickOpen,
        isReviewNotesPromptOpen,
        episodeFilePreviewOpen,
        isShareOpen,
        isTranscriptPreviewOpen,
        isMultitrackModalOpen,
        isProcessAudioPresetModalOpen,
        saving,
        notifying,
        episodeNotesQuickSaving,
        audioFile,
        artworkFile,
        globalMediaUploading,
        globalMediaUploadEpisodeId,
        canCreateLocalEpisode,
        canEditEpisodeNotes,
        closeModal,
        handleCancelSelectPodcast,
        closeNotifyModal,
        closeChangeLogModal,
        closeEpisodeNotesQuickModal,
        closeReviewNotesPrompt,
        closeShareModal,
        closeTranscriptPreviewModal,
        closeProcessAudioPresetModal,
        closeMultitrackModal,
        saveEpisodeNotesQuickModal,
        form.id,
    ]);

    useEffect(() => {
        let cancelled = false;

        async function loadClientEpisodeAudio() {
            if (!shouldShowEpisodeAudioSection || !form.id) {
                setClientEpisodeAudioUrl("");
                setClientEpisodeAudioLoading(false);
                setClientEpisodeAudioError("");
                setClientEpisodeAudioSource("");
                return;
            }

            setClientEpisodeAudioUrl("");
            setClientEpisodeAudioError("");
            setClientEpisodeAudioSource("");

            const captivateUrl = String(form.episode_mp3 || "").trim();
            if (/^https?:\/\//i.test(captivateUrl)) {
                setClientEpisodeAudioUrl(captivateUrl);
                setClientEpisodeAudioSource("captivate");
                return;
            }

            const selectedAudioItemId = String(form.selected_audio_file_item_id || "").trim();
            if (selectedAudioItemId) {
                setClientEpisodeAudioLoading(true);
                try {
                    const urlRes = await getEpisodeFileStreamUrl(form.id, selectedAudioItemId);
                    if (cancelled) return;
                    if (!urlRes?.ok || !urlRes?.stream_url) {
                        setClientEpisodeAudioError(
                            urlRes?.message || "Failed to load episode audio"
                        );
                        return;
                    }
                    setClientEpisodeAudioUrl(String(urlRes.stream_url));
                    setClientEpisodeAudioSource("sharepoint");
                } catch (err) {
                    if (cancelled) return;
                    setClientEpisodeAudioError(
                        err?.message || "Failed to load episode audio"
                    );
                } finally {
                    if (!cancelled) {
                        setClientEpisodeAudioLoading(false);
                    }
                }
                return;
            }

            setClientEpisodeAudioLoading(false);
        }

        loadClientEpisodeAudio();
        return () => {
            cancelled = true;
        };
    }, [
        shouldShowEpisodeAudioSection,
        form.id,
        form.selected_audio_file_item_id,
        form.episode_mp3,
    ]);

    const handleClientEpisodeAudioDownload = async () => {
        if (!shouldShowEpisodeAudioSection || !form.id) return;
        setClientEpisodeAudioError("");

        const captivateUrl = String(form.episode_mp3 || "").trim();
        if (/^https?:\/\//i.test(captivateUrl)) {
            const safeBase =
                String(form?.title || "episode-audio")
                    .trim()
                    .replace(/[^\w\- ]+/g, "")
                    .replace(/\s+/g, "-")
                    .toLowerCase() || "episode-audio";
            const anchor = document.createElement("a");
            anchor.href = captivateUrl;
            anchor.download = `${safeBase}.mp3`;
            anchor.rel = "noopener noreferrer";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            return;
        }

        const selectedAudioItemId = String(form.selected_audio_file_item_id || "").trim();
        if (clientEpisodeAudioSource === "sharepoint" && selectedAudioItemId) {
            const urlRes = await getEpisodeFileStreamUrl(form.id, selectedAudioItemId);
            if (!urlRes?.ok || !urlRes?.stream_url) {
                setClientEpisodeAudioError(urlRes?.message || "Failed to download audio");
                return;
            }

            const anchor = document.createElement("a");
            anchor.href = String(urlRes.stream_url);
            anchor.download =
                String(form.selected_audio_file_name || "").trim() || "episode-audio.mp3";
            anchor.rel = "noopener noreferrer";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
        }
    };

    // Change logs sorted newest → oldest
    const sortedChangeLogs = [...changeLogs].sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at) : 0;
        const db = b.created_at ? new Date(b.created_at) : 0;
        return db - da;
    });
    const hasAutoSwitchedClientDefaultFilterRef = useRef(false);

    useEffect(() => {
        const isDefaultClientView =
            isClientUser &&
            statusFilter === "not-published" &&
            !String(search || "").trim() &&
            !String(podcastFilter || "").trim();

        if (!isDefaultClientView) return;
        if (loading || !!error) return;
        if (hasAutoSwitchedClientDefaultFilterRef.current) return;

        if (visibleEpisodes.length === 0) {
            hasAutoSwitchedClientDefaultFilterRef.current = true;
            setStatusFilter("published");
        }
    }, [
        isClientUser,
        statusFilter,
        search,
        podcastFilter,
        loading,
        error,
        visibleEpisodes.length,
    ]);

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                {/* Header */}
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Episodes</h2>
                        <p className="portal-page__subtitle">
                            Review and manage upcoming and published episodes.
                        </p>
                    </div>

                    <button
                        onClick={handleOpenCreate}
                        className="btn btn--primary"
                        disabled={creatablePodcastIds.size === 0}
                    >
                        {currentUserRole === "client"
                            ? "+ Request Episode"
                            : "+ Create Episode"}
                    </button>
                </div>

                {/* Filters */}
                <div className="portal-filter-bar">
                    {/* Status filter */}
                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Status</span>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="portal-filter-bar__select"
                        >
                            <option value="not-published">
                                Not Published (default)
                            </option>
                            <option value="all">All</option>
                            <option value="requested">Requested</option>
                            <option value="planned">Planned</option>
                            <option value="pending approval">Pending Approval</option>
                            <option value="rejected">Rejected</option>
                            <option value="approved">Approved</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="published">Published</option>
                        </select>
                    </div>

                    {/* Podcast filter */}
                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Podcast</span>
                        <select
                            value={podcastFilter}
                            onChange={(e) => setPodcastFilter(e.target.value)}
                            className="portal-filter-bar__select"
                        >
                            <option value="">All podcasts</option>
                            {podcasts.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                ))}
                        </select>
                    </div>

                    {/* Search */}
                    <div className="portal-filter-bar__group portal-filter-bar__group--grow">
                        <span className="portal-filter-bar__label">Search</span>
                        <input
                            type="text"
                            placeholder="Search by title…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="portal-filter-bar__search"
                        />
                    </div>
                </div>

                {/* Errors */}
                {error && <div className="alert alert--error">{error}</div>}
                {podcastsError && (
                    <div className="alert alert--error">Podcasts: {podcastsError}</div>
                )}

                {loading && <p>Loading episodes…</p>}

                {!loading && visibleEpisodes.length === 0 && !error && (
                    <p>No episodes matching your filters 🎉</p>
                )}

                {/* Episodes as cards */}
                {!loading && visibleEpisodes.length > 0 && (
                    <div className="episode-list">
                        {visibleEpisodes.map((ep) => {
                            const statusColors = getStatusColor(ep.status);
                            const cardStatus = String(ep.status || "")
                                .toLowerCase()
                                .trim();
                            const canOpenReviewFromCard =
                                isClientUser &&
                                cardStatus === "pending approval" &&
                                String(ep?.review_token || "").trim() &&
                                Number(ep?.can_review_episodes ?? 1) === 1;
                            const isLocalOnlyEpisodeCard =
                                ep?.local_only_episode === true ||
                                Number(ep?.local_only_episode || 0) === 1;
                            const accent = canOpenReviewFromCard
                                ? "#16a34a"
                                : getEpisodeRowColor(ep);
                            const showPublishesMeta =
                                isAdminUser ||
                                (isDistributionEnabled(ep?.podcast_distribution) &&
                                    [
                                        "pending approval",
                                        "approved",
                                        "scheduled",
                                        "published",
                                    ].includes(cardStatus));

                            return (
                                <div
                                    key={ep.id}
                                    onClick={() => {
                                        if (canOpenReviewFromCard) {
                                            window.open(
                                                `#/review?token=${encodeURIComponent(
                                                    ep.review_token
                                                )}`,
                                                "_blank",
                                                "noopener,noreferrer"
                                            );
                                            return;
                                        }
                                        handleEditClick(ep.id);
                                    }}
                                    className="episode-card"
                                    style={{ borderLeftColor: accent }}
                                >
                                    <div className="episode-card__main">
                                        <h3 className="episode-card__title">{ep.title}</h3>

                                        <div className="episode-card__podcast">
                                            {(() => {
                                                const podcastName = String(
                                                    ep.podcast_name || ""
                                                ).trim();
                                                const episodeCode = formatEpisodeCodeLabel(
                                                    ep.season_number,
                                                    ep.episode_number
                                                );

                                                if (!podcastName) return "—";
                                                return episodeCode
                                                    ? `${podcastName} · ${episodeCode}`
                                                    : podcastName;
                                            })()}
                                        </div>

                                        {showPublishesMeta ? (
                                            isClientUser && !canOpenReviewFromCard ? (
                                                <div className="episode-card__meta" style={{ marginTop: 0 }}>
                                                    {(() => {
                                                        if (!ep.publication_date) {
                                                            return "No publication date set";
                                                        }
                                                        const dateStr = formatPublishDateLabel(
                                                            ep.publication_date
                                                        );
                                                        const rawTime = ep.publication_time
                                                            ? String(ep.publication_time)
                                                            : "";
                                                        const timeStr = rawTime
                                                            ? rawTime.slice(0, 5)
                                                            : "";
                                                        if (timeStr) {
                                                            return `Publishes: ${dateStr} at ${timeStr}`;
                                                        }
                                                        return `Publishes: ${dateStr}`;
                                                    })()}
                                                </div>
                                            ) : (
                                                <div className="episode-card__meta">
                                                    {(() => {
                                                        const adminUsesPublication =
                                                            isAdminUser &&
                                                            isDistributionEnabled(
                                                                ep?.podcast_distribution
                                                            );
                                                        if (!ep.publication_date) {
                                                            return isAdminUser &&
                                                                !adminUsesPublication
                                                                ? "No deadline date set"
                                                                : "No publication date set";
                                                        }

                                                        const dateStr =
                                                            formatPublishDateLabel(
                                                                ep.publication_date
                                                            );

                                                        if (isAdminUser && !adminUsesPublication) {
                                                            return `Deadline: ${dateStr}`;
                                                        }

                                                        const rawTime = ep.publication_time
                                                            ? String(ep.publication_time)
                                                            : "";
                                                        const timeStr = rawTime
                                                            ? rawTime.slice(0, 5)
                                                            : "";

                                                        if (timeStr) {
                                                            return `Publishes: ${dateStr} at ${timeStr}`;
                                                        }

                                                        return `Publishes: ${dateStr}`;
                                                    })()}
                                                </div>
                                            )
                                        ) : (
                                            <div
                                                className="episode-card__meta"
                                                style={{ visibility: "hidden" }}
                                                aria-hidden="true"
                                            >
                                                Publishes: 00/00/0000 at 00:00
                                            </div>
                                        )}

                                        {currentUserRole !== "client" &&
                                        ep.has_client_suggested_changes ? (
                                            <div
                                                className="episode-card__meta"
                                                style={{ marginTop: 4, fontSize: 12 }}
                                            >
                                                📌 Client suggested changes pending
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="episode-card__right">
                                        {isAdminUser && isLocalOnlyEpisodeCard ? (
                                            <span
                                                className="episode-card__status-pill"
                                                style={{
                                                    background: "#dbeafe",
                                                    color: "#1e3a8a",
                                                }}
                                            >
                                                Local only
                                            </span>
                                        ) : null}
                                        {canOpenReviewFromCard ? (
                                                <a
                                                    className="episode-card__status-pill"
                                                    href={`#/review?token=${encodeURIComponent(
                                                        ep.review_token
                                                    )}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        background: "#dcfce7",
                                                        color: "#166534",
                                                        textDecoration: "none",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    Episode ready for review
                                                </a>
                                            ) : (
                                                <span
                                                    className="episode-card__status-pill"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "—"}
                                                </span>
                                            )
                                            }
                                        {(isAdminUser ||
                                            Number(ep?.can_upload_episode_files ?? 0) === 1) &&
                                        String(ep?.sharepoint_folder_path || "").trim() ? (
                                                <button
                                                    type="button"
                                                    className="episode-card__action-pill"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenFilesClick(ep);
                                                    }}
                                                >
                                                    Files
                                                </button>
                                            ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ---------- SELECT PODCAST MODAL (for new episodes) ---------- */}
            {isSelectPodcastOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Create New Episode</h3>
                                <p className="modal__subtitle">
                                    Select the podcast this episode belongs to.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleCancelSelectPodcast}
                                className="modal__close-btn"
                            >
                                ✕
                            </button>
                        </div>

                        {selectPodcastError && (
                            <div className="alert alert--error">
                                {selectPodcastError}
                            </div>
                        )}

                        <div className="modal__body">
                            <fieldset className="fieldset">
                                <legend className="fieldset__legend">Podcast</legend>
                                <label className="form-field">
                                    <span className="form-field__label">Podcast</span>
                                    <select
                                        value={selectedPodcastId}
                                        onChange={(e) =>
                                            setSelectedPodcastId(e.target.value)
                                        }
                                        className="form-field__select"
                                    >
                                        <option value="">Select a podcast</option>
                                        {podcasts
                                            .filter((p) =>
                                                creatablePodcastIds.has(String(p.id))
                                            )
                                            .map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </fieldset>
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={handleCancelSelectPodcast}
                                className="btn btn--secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmPodcast}
                                className="btn btn--primary"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- OPENING EPISODE LOADING MODAL ---------- */}
            {openingEpisodeId !== null && !isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Opening episode…</h3>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- OPENING FILES LOADING MODAL ---------- */}
            {openingFilesEpisodeId !== null && !isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Opening files…</h3>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- MODAL FOR EDITING / CREATING EPISODE ---------- */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">
                                    {isFilesOnlyMode
                                        ? "Episode Files"
                                        : isEditing
                                            ? "Edit Episode"
                                            : "Create Episode"}
                                </h3>
                                <p className="modal__subtitle">
                                    {isFilesOnlyMode
                                        ? "Upload and manage files for this episode."
                                        : isRequestedCreateFlow
                                        ? "Request a new episode for this podcast."
                                        : "Manage details, scheduling and content."}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={closeModal}
                                className="modal__close-btn"
                            >
                                ✕
                            </button>
                        </div>

                        {modalError && (
                            <div className="alert alert--error">{modalError}</div>
                        )}
                        {!isFilesOnlyMode && !canSaveEpisode && !isRequestedCreateFlow && (
                            <div className="alert">
                                You have read-only access for this podcast. Editing is disabled.
                            </div>
                        )}

                        <form
                            onSubmit={handleSave}
                            className="modal__body"
                            ref={episodeFormRef}
                        >
                            {!isFilesOnlyMode && (
                            <div
                                className={
                                    "modal__grid" +
                                    (hasRightColumnContent
                                        ? " modal__grid--two-columns"
                                        : "")
                                }
                            >
                                {/* Left column */}
                                <div className="modal__column">
                                    {/* Basic Info */}
                                    <fieldset
                                        className="fieldset"
                                        disabled={isRequestedCreateFlow ? false : isEpisodeReadOnly}
                                    >
                                        <legend className="fieldset__legend">
                                            Basic Info
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">Title</span>
                                            <input
                                                name="title"
                                                value={form.title}
                                                onChange={handleChange}
                                                required
                                                className="form-field__input"
                                            />
                                        </label>

                                        {!isRequestedCreateFlow && shouldShowExtendedMetadataFields && (
                                            <>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <label
                                                        className="form-field"
                                                        style={{ flex: 1 }}
                                                    >
                                                        <span className="form-field__label">
                                                            Season #
                                                        </span>
                                                        <input
                                                            name="season_number"
                                                            value={form.season_number}
                                                            onChange={handleChange}
                                                            className="form-field__input"
                                                        />
                                                    </label>

                                                    <label
                                                        className="form-field"
                                                        style={{ flex: 1 }}
                                                    >
                                                        <span className="form-field__label">
                                                            Episode #
                                                        </span>
                                                        <input
                                                            name="episode_number"
                                                            value={form.episode_number}
                                                            onChange={handleChange}
                                                            className="form-field__input"
                                                        />
                                                    </label>
                                                </div>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    {!shouldHidePublicationMetaForClient && (
                                                        <label
                                                            className="form-field"
                                                            style={{ flex: 1 }}
                                                        >
                                                            <span className="form-field__label">
                                                                {isAdminUser &&
                                                                !isDistributionEnabledForForm
                                                                    ? "Deadline Date"
                                                                    : "Publication Date"}
                                                            </span>
                                                            <input
                                                                type="date"
                                                                name="publication_date"
                                                                value={form.publication_date || ""}
                                                                onChange={handleChange}
                                                                className="form-field__input"
                                                            />
                                                        </label>
                                                    )}

                                                    {isAdminUser &&
                                                    isDistributionEnabledForForm &&
                                                    !isRequestedCreateFlow && (
                                                        <label
                                                            className="form-field"
                                                            style={{ flex: 1 }}
                                                        >
                                                            <span className="form-field__label">
                                                                Publication Time
                                                            </span>
                                                            <input
                                                                name="publication_time"
                                                                value={form.publication_time || ""}
                                                                onChange={handleChange}
                                                                placeholder="e.g. 09:00"
                                                                className="form-field__input"
                                                            />
                                                        </label>
                                                    )}
                                                </div>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <label
                                                        className="form-field"
                                                        style={{ flex: 1 }}
                                                    >
                                                        <span className="form-field__label">
                                                            Status
                                                        </span>
                                                        <select
                                                            name="status"
                                                            value={form.status}
                                                            onChange={handleChange}
                                                            className="form-field__select"
                                                        >
                                                            <option value="Requested">
                                                                Requested
                                                            </option>
                                                            <option value="Planned">
                                                                Planned
                                                            </option>
                                                            <option value="Pending Approval">
                                                                Pending Approval
                                                            </option>
                                                            <option value="Rejected">
                                                                Rejected
                                                            </option>
                                                            <option value="Approved">
                                                                Approved
                                                            </option>
                                                            <option value="Scheduled">
                                                                Scheduled
                                                            </option>
                                                            <option value="Published">
                                                                Published
                                                            </option>
                                                        </select>
                                                    </label>

                                                    {!isClientUser && (
                                                        <label
                                                            className="form-field"
                                                            style={{ flex: 1 }}
                                                        >
                                                            <span className="form-field__label">
                                                                Episode Type
                                                            </span>
                                                            <select
                                                                name="episode_type"
                                                                value={form.episode_type}
                                                                onChange={handleChange}
                                                                className="form-field__select"
                                                            >
                                                                <option value="Normal">
                                                                    Normal
                                                                </option>
                                                                <option value="Trailer">
                                                                    Trailer
                                                                </option>
                                                                <option value="Bonus">
                                                                    Bonus
                                                                </option>
                                                            </select>
                                                        </label>
                                                    )}
                                                </div>

                                            </>
                                        )}
                                    </fieldset>

                                    {canCreateLocalEpisode && (
                                        <fieldset className="fieldset">
                                            <legend className="fieldset__legend">Settings</legend>
                                            <label
                                                className="form-field"
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    gap: 8,
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={createWithoutCaptivateSync}
                                                    onChange={(e) =>
                                                        setCreateWithoutCaptivateSync(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                <span className="form-field__label">
                                                    Create local episode (skip Captivate sync, email sends, and SharePoint folder auto-create)
                                                </span>
                                            </label>
                                        </fieldset>
                                    )}

                                    {/* Episode Notes (admin placement) */}
                                    {isAdminUser && !isRequestedCreateFlow && (
                                        <fieldset
                                            className="fieldset"
                                            disabled={!canEditEpisodeNotes}
                                        >
                                            <legend className="fieldset__legend">
                                                Episode Notes
                                            </legend>
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Any additional information you want us
                                                    to know about for this episode
                                                </span>
                                                <textarea
                                                    name="notes"
                                                    value={form.notes}
                                                    onChange={handleChange}
                                                    rows={3}
                                                    className="form-field__textarea"
                                                />
                                            </label>
                                        </fieldset>
                                    )}
                                </div>

                                {/* Right column */}
                                {hasRightColumnContent && (
                                    <div className="modal__column">
                                    {/* Media */}
                                    {showMediaFieldset && (
                                        <fieldset
                                            className="fieldset"
                                            disabled={isEpisodeReadOnly}
                                        >
                                            <legend className="fieldset__legend">
                                                Media
                                            </legend>

                                            {canShowCaptivateMediaUploads &&
                                                (isEditing ? (
                                                    <>
                                                        <label className="form-field">
                                                            <span className="form-field__label">
                                                                Upload New Audio (Captivate)
                                                            </span>
                                                            <input
                                                                type="file"
                                                                accept="audio/*"
                                                                onChange={
                                                                    handleAudioFileChange
                                                                }
                                                                className="form-field__file"
                                                                disabled={isEpisodeReadOnly}
                                                            />
                                                        </label>
                                                        {form.selected_audio_file_item_id && (
                                                            <p
                                                                className="text-description"
                                                                style={{ marginTop: -4, marginBottom: 8 }}
                                                            >
                                                                Selected from Episode Files:{" "}
                                                                <strong>
                                                                    {form.selected_audio_file_name ||
                                                                        "Audio file"}
                                                                </strong>
                                                            </p>
                                                        )}

                                                        <label className="form-field">
                                                            <span className="form-field__label">
                                                                Upload New Artwork (Captivate)
                                                            </span>
                                                            <input
                                                                type="file"
                                                                accept="image/png,image/jpeg"
                                                                onChange={
                                                                    handleArtworkFileChange
                                                                }
                                                                className="form-field__file"
                                                                disabled={isEpisodeReadOnly}
                                                            />
                                                        </label>
                                                        {form.selected_artwork_file_item_id && (
                                                            <p
                                                                className="text-description"
                                                                style={{ marginTop: -4, marginBottom: 8 }}
                                                            >
                                                                Selected from Episode Files:{" "}
                                                                <strong>
                                                                    {form.selected_artwork_file_name ||
                                                                        "Artwork file"}
                                                                </strong>
                                                            </p>
                                                        )}

                                                        {(form.last_audio_uploaded_path ||
                                                            form.last_media_uploaded ||
                                                            form.last_artwork_uploaded_path ||
                                                            form.last_artwork_uploaded) && (
                                                                <div
                                                                    style={{
                                                                        marginTop: 8,
                                                                        paddingTop: 8,
                                                                        borderTop:
                                                                            "1px solid #e5e7eb",
                                                                        fontSize: 12,
                                                                        color: "#6b7280",
                                                                    }}
                                                                >
                                                                    {(form.last_audio_uploaded_path ||
                                                                        form.last_media_uploaded) && (
                                                                            <p
                                                                                className="text-description"
                                                                                style={{
                                                                                    margin: 0,
                                                                                    marginBottom: 4,
                                                                                }}
                                                                            >
                                                                                <strong>
                                                                                    Last audio upload:
                                                                                </strong>{" "}
                                                                                {decodeFilename(
                                                                                    form.last_audio_uploaded_path
                                                                                ) ||
                                                                                    "Unknown file"}
                                                                                {form.last_media_uploaded && (
                                                                                    <>
                                                                                        {" · "}
                                                                                        {new Date(
                                                                                            form.last_media_uploaded
                                                                                        ).toLocaleString(
                                                                                            "en-GB"
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </p>
                                                                        )}

                                                                    {(form.last_artwork_uploaded_path ||
                                                                        form.last_artwork_uploaded) && (
                                                                            <p
                                                                                className="text-description"
                                                                                style={{ margin: 0 }}
                                                                            >
                                                                                <strong>
                                                                                    Last artwork upload:
                                                                                </strong>{" "}
                                                                                {decodeFilename(
                                                                                    form.last_artwork_uploaded_path
                                                                                ) ||
                                                                                    "Unknown file"}
                                                                                {form.last_artwork_uploaded && (
                                                                                    <>
                                                                                        {" · "}
                                                                                        {new Date(
                                                                                            form.last_artwork_uploaded
                                                                                        ).toLocaleString(
                                                                                            "en-GB"
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </p>
                                                                        )}
                                                                </div>
                                                            )}
                                                    </>
                                                ) : (
                                                    <p className="text-description">
                                                        You can upload audio and artwork
                                                        after creating the episode.
                                                    </p>
                                                ))}

                                            {canShowCaptivateMediaUploads && (
                                                <label className="form-field__checkbox-row">
                                                    <input
                                                        type="checkbox"
                                                        name="explicit"
                                                        checked={form.explicit}
                                                        onChange={handleCheckboxChange}
                                                    />
                                                    <span className="form-field__label">
                                                        Explicit content
                                                    </span>
                                                </label>
                                            )}

                                            {showSharePointFolderEditor && (
                                                <label
                                                    className="form-field"
                                                    style={{ marginTop: 10 }}
                                                >
                                                    <span className="form-field__label">
                                                        SharePoint Episode Folder
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={sharePointEpisodeFolderName}
                                                        name="sharepoint_folder_path"
                                                        onChange={(e) =>
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                sharepoint_folder_path:
                                                                    e.target.value,
                                                            }))
                                                        }
                                                        className="form-field__input"
                                                        placeholder="Not created yet"
                                                    />
                                                </label>
                                            )}
                                        </fieldset>
                                    )}

                                    {/* Notifications (edit only) */}
                                    {!isClientUser && canSendNotifications && isEditing && (
                                        <fieldset
                                            className="fieldset"
                                            style={
                                                form.captivate_episode_id
                                                    ? { marginTop: 12 }
                                                    : undefined
                                            }
                                        >
                                            <legend className="fieldset__legend">
                                                Notifications
                                            </legend>

                                            <p
                                                className="text-description"
                                                style={{ marginTop: 0 }}
                                            >
                                                Send a note to the podcast owners for
                                                this episode.
                                            </p>

                                            <button
                                                type="button"
                                                onClick={openNotifyModal}
                                                className="btn btn--secondary"
                                                disabled={notifying}
                                            >
                                                Send notification
                                            </button>
                                        </fieldset>
                                    )}

                                    {/* Client Review + Suggested Changes */}
                                    {!isClientUser && isEditing && hasClientReviewData && (
                                        <fieldset
                                            className="fieldset"
                                            style={{ marginTop: 12 }}
                                        >
                                            <legend className="fieldset__legend">
                                                Client Review
                                            </legend>

                                            {form.client_reviewed_at ? (
                                                <p
                                                    className="text-description"
                                                    style={{ marginTop: 0 }}
                                                >
                                                    Reviewed on{" "}
                                                    <strong>
                                                        {formatDateTime(
                                                            form.client_reviewed_at
                                                        )}
                                                    </strong>
                                                    .
                                                </p>
                                            ) : (
                                                <p
                                                    className="text-description"
                                                    style={{ marginTop: 0 }}
                                                >
                                                    Client feedback attachments were uploaded
                                                    via the review link.
                                                </p>
                                            )}

                                            {form.client_review_comments ? (
                                                <label
                                                    className="form-field"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    <span className="form-field__label">
                                                        Client comments
                                                    </span>
                                                    <textarea
                                                        className="form-field__textarea"
                                                        rows={4}
                                                        value={
                                                            form.client_review_comments
                                                        }
                                                        readOnly
                                                    />
                                                </label>
                                            ) : form.client_reviewed_at ? (
                                                <p
                                                    className="text-description"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    {String(form.status || "")
                                                        .trim()
                                                        .toLowerCase() === "rejected"
                                                        ? "The client rejected this episode without leaving any comments."
                                                        : String(form.status || "")
                                                              .trim()
                                                              .toLowerCase() === "approved"
                                                          ? "The client approved this episode without leaving any comments."
                                                          : "The client reviewed this episode without leaving any comments."}
                                                </p>
                                            ) : null}

                                            {hasClientReviewAttachments && (
                                                    <div style={{ marginTop: 10 }}>
                                                        <span className="form-field__label">
                                                            Client attachments
                                                        </span>
                                                        <div
                                                            style={{
                                                                marginTop: 6,
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                gap: 6,
                                                            }}
                                                        >
                                                            {form.client_review_attachments.map(
                                                                (file, index) => (
                                                                    <div
                                                                        key={`${file?.name || "file"}-${file?.uploaded_at || ""}-${index}`}
                                                                        className="text-description"
                                                                        style={{
                                                                            margin: 0,
                                                                            fontSize: 12,
                                                                        }}
                                                                    >
                                                                        <strong>
                                                                            {file?.name || "Attachment"}
                                                                        </strong>
                                                                        {file?.uploaded_at
                                                                            ? ` · ${formatDateTime(file.uploaded_at)}`
                                                                            : ""}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                            {hasClientSuggestedChanges && (
                                                <div
                                                    style={{
                                                        marginTop: 12,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 4,
                                                    }}
                                                >
                                                    <p
                                                        className="text-description"
                                                        style={{
                                                            margin: 0,
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        The client has suggested changes
                                                        to the title and/or show notes.
                                                    </p>
                                                    <button
                                                        type="button"
                                                        className="btn btn--secondary btn--small"
                                                        onClick={openSuggestModal}
                                                    >
                                                        Review suggested changes
                                                    </button>
                                                </div>
                                            )}
                                        </fieldset>
                                    )}
                                    </div>
                                )}
                            </div>
                            )}

                            {shouldShowEpisodeAudioSection && (
                                <fieldset className="fieldset" style={{ marginTop: 12 }}>
                                    <legend className="fieldset__legend">Episode Audio</legend>

                                    {clientEpisodeAudioLoading ? (
                                        <p className="text-description">Loading audio…</p>
                                    ) : null}

                                    {!clientEpisodeAudioLoading && clientEpisodeAudioError ? (
                                        <div className="alert alert--error">
                                            {clientEpisodeAudioError}
                                        </div>
                                    ) : null}

                                    {!clientEpisodeAudioLoading &&
                                    !clientEpisodeAudioError &&
                                    clientEpisodeAudioUrl ? (
                                        <div style={{ width: "100%" }}>
                                            <AudioPlayer
                                                src={clientEpisodeAudioUrl}
                                                onDownload={handleClientEpisodeAudioDownload}
                                            />
                                        </div>
                                    ) : null}

                                    {!clientEpisodeAudioLoading &&
                                    !clientEpisodeAudioError &&
                                    !clientEpisodeAudioUrl ? (
                                        <p className="text-description">No audio available.</p>
                                    ) : null}
                                </fieldset>
                            )}

                            {!isFilesOnlyMode &&
                                !isRequestedCreateFlow &&
                                shouldShowShowNotesSection && (
                                <fieldset
                                    className="fieldset"
                                    style={{ marginTop: 12 }}
                                >
                                    <legend className="fieldset__legend">
                                        Show Notes
                                    </legend>
                                    <div style={{ marginTop: 4 }}>
                                        <ReactQuill
                                            className="portal-quill-editor"
                                            value={form.show_notes_captivate}
                                            onChange={handleShowNotesChange}
                                            modules={isShowNotesReadOnly ? { toolbar: false } : quillModules}
                                            formats={quillFormats}
                                            readOnly={isShowNotesReadOnly}
                                        />
                                    </div>

                                </fieldset>
                            )}

                            {isEditing && canAccessEpisodeFiles && (
                                <fieldset className="fieldset" style={{ marginTop: 12 }}>
                                    <legend className="fieldset__legend">Episode Files</legend>

                                    <div
                                        className={
                                            "file-explorer" +
                                            (episodeFilesDragOver ? " file-explorer--drag-over" : "")
                                        }
                                        onDragEnter={handleEpisodeFilesDragEnter}
                                        onDragOver={handleEpisodeFilesDragOver}
                                        onDragLeave={handleEpisodeFilesDragLeave}
                                        onDrop={handleEpisodeFilesDrop}
                                    >
                                        {episodeFilesDragOver && episodeFilesCanUpload && (
                                            <div className="file-explorer__drop-hint">
                                                Drop files to upload into this folder
                                            </div>
                                        )}

                                        <div className="file-explorer__command-bar">
                                        {episodeFilesCanCreateFolder && (
                                            <button
                                                type="button"
                                                className="btn btn--primary btn--small"
                                                onClick={handleEpisodeCreateFolder}
                                                disabled={
                                                    episodeFilesLoading ||
                                                    episodeFilesUploading ||
                                                    episodeFilesBusyAction
                                                }
                                            >
                                                New folder
                                            </button>
                                        )}

                                        {episodeFilesCanUpload && (
                                            <button
                                                type="button"
                                                className="btn btn--secondary btn--small"
                                                onClick={() => episodeFileInputRef.current?.click()}
                                                disabled={
                                                    episodeFilesLoading ||
                                                    episodeFilesUploading ||
                                                    episodeFilesBusyAction
                                                }
                                            >
                                                {episodeFilesUploading
                                                    ? `Uploading… ${episodeFilesUploadProgress}%`
                                                    : "Upload files"}
                                            </button>
                                        )}

                                        {isAdminUser && (
                                            <button
                                                type="button"
                                                className="btn btn--secondary btn--small"
                                                onClick={handleOpenMultitrackModal}
                                                disabled={
                                                    episodeFilesLoading ||
                                                    episodeFilesUploading ||
                                                    episodeFilesBusyAction ||
                                                    transcribingAudio ||
                                                    processingAudio ||
                                                    generatingShowNotes ||
                                                    multitrackProcessing
                                                }
                                            >
                                                {multitrackProcessing
                                                    ? "Processing multitrack…"
                                                    : "Create multitrack production"}
                                            </button>
                                        )}

                                        <input
                                            ref={episodeFileInputRef}
                                            type="file"
                                            multiple
                                            onChange={(e) => {
                                                const picked = Array.from(e.target.files || []);
                                                e.target.value = "";
                                                if (picked.length) {
                                                    handleEpisodeUploadFiles(picked);
                                                }
                                            }}
                                            className="file-explorer__upload-input"
                                            disabled={
                                                !episodeFilesCanUpload ||
                                                episodeFilesLoading ||
                                                episodeFilesUploading ||
                                                episodeFilesBusyAction
                                            }
                                        />

                                        <button
                                            type="button"
                                            className="btn btn--secondary btn--small"
                                            onClick={() => loadEpisodeFiles(form.id, episodeFilesPath)}
                                            disabled={
                                                episodeFilesLoading ||
                                                episodeFilesUploading ||
                                                episodeFilesBusyAction
                                            }
                                        >
                                            Refresh
                                        </button>
                                        </div>

                                        <div className="file-explorer__path-bar">
                                        <button
                                            type="button"
                                            className="btn btn--secondary btn--small"
                                            onClick={goUpEpisodePath}
                                            disabled={episodeFilesPath === "/" || episodeFilesLoading}
                                        >
                                            Up
                                        </button>

                                        <div className="file-explorer__breadcrumbs">
                                            {episodeFilesBreadcrumbs.map((crumb, index) => (
                                                <button
                                                    key={crumb.path}
                                                    type="button"
                                                    className={
                                                        "file-explorer__crumb" +
                                                        (index === episodeFilesBreadcrumbs.length - 1
                                                            ? " file-explorer__crumb--active"
                                                            : "")
                                                    }
                                                    onClick={() => goToEpisodePath(crumb.path)}
                                                    disabled={
                                                        episodeFilesLoading ||
                                                        index === episodeFilesBreadcrumbs.length - 1
                                                    }
                                                >
                                                    {crumb.label}
                                                </button>
                                            ))}
                                        </div>

                                        <input
                                            type="text"
                                            className="file-explorer__search"
                                            placeholder="Search this folder"
                                            value={episodeFilesSearch}
                                            onChange={(e) => setEpisodeFilesSearch(e.target.value)}
                                        />
                                        </div>

                                        {!episodeFilesCanUpload &&
                                            !episodeFilesCanCreateFolder &&
                                            !episodeFilesCanRenameDelete && (
                                        <p className="text-description file-explorer__selected-file">
                                            This folder is read-only.
                                        </p>
                                            )}

                                        {episodeFilesError && (
                                        <div className="alert alert--error">{episodeFilesError}</div>
                                        )}
                                        {episodeFilesLoading && <p>Loading files…</p>}

                                        {!episodeFilesLoading && !episodeFilesError && episodeVisibleFiles.length === 0 && (
                                        <p className="text-description">
                                            No files or folders in this location.
                                        </p>
                                        )}

                                        {!episodeFilesLoading && !episodeFilesError && episodeVisibleFiles.length > 0 && (
                                        <table className="change-log-table file-explorer__table">
                                            <colgroup>
                                                <col style={{ width: "58%" }} />
                                                <col style={{ width: "9%" }} />
                                                <col style={{ width: "28%" }} />
                                                <col style={{ width: "5%" }} />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <button
                                                            type="button"
                                                            className="file-explorer__name-link"
                                                            onClick={() => toggleEpisodeFilesSort("name")}
                                                        >
                                                            {`Name${episodeFilesSortIndicator("name")}`}
                                                        </button>
                                                    </th>
                                                    <th className="file-explorer__col-mobile-hide">
                                                        <button
                                                            type="button"
                                                            className="file-explorer__name-link"
                                                            onClick={() => toggleEpisodeFilesSort("size")}
                                                        >
                                                            {`Size${episodeFilesSortIndicator("size")}`}
                                                        </button>
                                                    </th>
                                                    <th className="file-explorer__col-mobile-hide">
                                                        <button
                                                            type="button"
                                                            className="file-explorer__name-link"
                                                            onClick={() => toggleEpisodeFilesSort("modified")}
                                                        >
                                                            {`Modified${episodeFilesSortIndicator("modified")}`}
                                                        </button>
                                                    </th>
                                                    <th aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {episodeVisibleFiles.map((item) => {
                                                    const isSelectedEpisodeAudio =
                                                        item.type === "file" &&
                                                        String(item.id || "") ===
                                                            String(
                                                                form.selected_audio_file_item_id || ""
                                                            );
                                                    const isSelectedEpisodeArtwork =
                                                        item.type === "file" &&
                                                        String(item.id || "") ===
                                                            String(
                                                                form.selected_artwork_file_item_id || ""
                                                            );
                                                    return (
                                                    <tr
                                                        key={item.id}
                                                        className={
                                                            item.type === "folder" &&
                                                            String(item.id || "") ===
                                                                episodeFilesFolderDropTargetId
                                                                ? "file-explorer__row file-explorer__row--drop-target"
                                                                : "file-explorer__row"
                                                        }
                                                        draggable={episodeFilesCanRenameDelete}
                                                        onDragStart={(e) =>
                                                            handleEpisodeItemDragStart(e, item)
                                                        }
                                                        onDragEnd={handleEpisodeItemDragEnd}
                                                        onDragEnter={(e) => {
                                                            if (
                                                                !episodeFilesCanUpload &&
                                                                !episodeFilesCanRenameDelete
                                                            ) {
                                                                return;
                                                            }
                                                            if (!item || item.type !== "folder") return;
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setEpisodeFilesFolderDropTargetId(
                                                                String(item.id || "")
                                                            );
                                                        }}
                                                        onDragOver={(e) => {
                                                            if (
                                                                !episodeFilesCanUpload &&
                                                                !episodeFilesCanRenameDelete
                                                            ) {
                                                                return;
                                                            }
                                                            if (!item || item.type !== "folder") return;
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (e.dataTransfer) {
                                                                e.dataTransfer.dropEffect = "move";
                                                            }
                                                            setEpisodeFilesFolderDropTargetId(
                                                                String(item.id || "")
                                                            );
                                                        }}
                                                        onDragLeave={(e) => {
                                                            if (!item || item.type !== "folder") return;
                                                            const next = e.relatedTarget;
                                                            if (
                                                                next &&
                                                                e.currentTarget.contains(next)
                                                            ) {
                                                                return;
                                                            }
                                                            setEpisodeFilesFolderDropTargetId(
                                                                (current) =>
                                                                    current ===
                                                                        String(item.id || "")
                                                                        ? ""
                                                                        : current
                                                            );
                                                        }}
                                                        onDrop={(e) => {
                                                            if (!item || item.type !== "folder") return;
                                                            handleEpisodeFolderDrop(e, item);
                                                        }}
                                                    >
                                                        <td>
                                                            <div className="file-explorer__name-cell">
                                                                <span className="file-explorer__name-icon">
                                                                    {item.type === "folder" ? "📁" : "📄"}
                                                                </span>
                                                                {item.type === "folder" ? (
                                                                    <button
                                                                        type="button"
                                                                        className="file-explorer__name-link"
                                                                        onClick={() => openEpisodeFolder(item)}
                                                                        disabled={episodeFilesBusyAction}
                                                                    >
                                                                        {item.name}
                                                                    </button>
                                                                ) : (
                                                                    <span>{item.name}</span>
                                                                )}
                                                                {(isSelectedEpisodeAudio ||
                                                                    isSelectedEpisodeArtwork) && (
                                                                    <span className="file-explorer__usage-badges">
                                                                        {isSelectedEpisodeAudio && (
                                                                            <span className="file-explorer__usage-badge file-explorer__usage-badge--audio">
                                                                                Episode Audio
                                                                            </span>
                                                                        )}
                                                                        {isSelectedEpisodeArtwork && (
                                                                            <span className="file-explorer__usage-badge file-explorer__usage-badge--artwork">
                                                                                Episode Artwork
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="file-explorer__col-mobile-hide">
                                                            {item.type === "file"
                                                                ? formatFileSize(item.size)
                                                                : "—"}
                                                        </td>
                                                        <td className="file-explorer__col-mobile-hide">
                                                            {formatModified(item.last_modified)}
                                                        </td>
                                                        <td>
                                                            <div
                                                                className="file-explorer__actions"
                                                                style={{
                                                                    position: "relative",
                                                                    display: "flex",
                                                                    justifyContent: "flex-end",
                                                                    overflow: "visible",
                                                                }}
                                                            >
                                                                {renderEpisodeFileRowActions(item)}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        )}

                                        <div className="episode-external-links">
                                            <div className="episode-external-links__header">
                                                <h4 className="episode-external-links__title">
                                                    External Links
                                                </h4>
                                                <p
                                                    className="text-description"
                                                    style={{ margin: 0 }}
                                                >
                                                    Add share links or references for this
                                                    episode.
                                                </p>
                                            </div>

                                            {episodeFilesCanUpload && (
                                                <div className="episode-external-links__form">
                                                    <input
                                                        type="text"
                                                        className="form-field__input"
                                                        placeholder="Link title"
                                                        value={episodeExternalLinkTitle}
                                                        onChange={(e) =>
                                                            setEpisodeExternalLinkTitle(
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={episodeExternalLinksBusy}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-field__input"
                                                        placeholder="Link URL"
                                                        value={episodeExternalLinkUrl}
                                                        onChange={(e) =>
                                                            setEpisodeExternalLinkUrl(
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={episodeExternalLinksBusy}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-field__input"
                                                        placeholder="Notes (optional)"
                                                        value={episodeExternalLinkNotes}
                                                        onChange={(e) =>
                                                            setEpisodeExternalLinkNotes(
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={episodeExternalLinksBusy}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn--primary btn--small"
                                                        onClick={handleAddEpisodeExternalLink}
                                                        disabled={episodeExternalLinksBusy}
                                                    >
                                                        Add link
                                                    </button>
                                                </div>
                                            )}

                                            {episodeExternalLinksError && (
                                                <div className="alert alert--error">
                                                    {episodeExternalLinksError}
                                                </div>
                                            )}

                                            {episodeExternalLinksLoading && (
                                                <p
                                                    className="text-description"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Loading links…
                                                </p>
                                            )}

                                            {!episodeExternalLinksLoading &&
                                                episodeExternalLinks.length === 0 && (
                                                    <p
                                                        className="text-description"
                                                        style={{ marginTop: 8 }}
                                                    >
                                                        No external links added yet.
                                                    </p>
                                                )}

                                            {!episodeExternalLinksLoading &&
                                                episodeExternalLinks.length > 0 && (
                                                    <table className="change-log-table file-explorer__table">
                                                        <colgroup>
                                                            <col style={{ width: "30%" }} />
                                                            <col style={{ width: "46%" }} />
                                                            <col style={{ width: "24%" }} />
                                                        </colgroup>
                                                        <thead>
                                                            <tr>
                                                                <th>Title</th>
                                                                <th>URL</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {episodeExternalLinks.map((link) => {
                                                                const safeExternalUrl =
                                                                    normalizeExternalLinkUrl(
                                                                        link.url
                                                                    );
                                                                return (
                                                                <tr key={link.id}>
                                                                    <td>{link.title}</td>
                                                                    <td>
                                                                        {safeExternalUrl ? (
                                                                            <a
                                                                                href={safeExternalUrl}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="file-explorer__name-link"
                                                                            >
                                                                                {link.url}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{link.url}</span>
                                                                        )}
                                                                        {link.notes ? (
                                                                            <div
                                                                                className="text-description"
                                                                                style={{
                                                                                    marginTop: 4,
                                                                                    fontSize: 12,
                                                                                }}
                                                                            >
                                                                                {link.notes}
                                                                            </div>
                                                                        ) : null}
                                                                    </td>
                                                                    <td>
                                                                        <div className="file-explorer__actions">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn--secondary btn--tiny"
                                                                                onClick={() =>
                                                                                    safeExternalUrl &&
                                                                                    window.open(
                                                                                        safeExternalUrl,
                                                                                        "_blank",
                                                                                        "noopener,noreferrer"
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    episodeExternalLinksBusy ||
                                                                                    !safeExternalUrl
                                                                                }
                                                                            >
                                                                                Open
                                                                            </button>
                                                                            {episodeFilesCanRenameDelete && (
                                                                                <>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn--secondary btn--tiny"
                                                                                        onClick={() =>
                                                                                            handleEditEpisodeExternalLink(
                                                                                                link
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            episodeExternalLinksBusy
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn--danger btn--tiny"
                                                                                        onClick={() =>
                                                                                            handleDeleteEpisodeExternalLink(
                                                                                                link
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            episodeExternalLinksBusy
                                                                                        }
                                                                                    >
                                                                                        Delete
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                )}
                                        </div>
                                    </div>
                                </fieldset>
                            )}

                            {/* Episode Notes (client placement: below Episode Files) */}
                            {!isFilesOnlyMode && isClientUser && !isRequestedCreateFlow && (
                                <fieldset
                                    className="fieldset"
                                    style={{ marginTop: 12 }}
                                    disabled={!canEditEpisodeNotes}
                                >
                                    <legend className="fieldset__legend">
                                        Episode Notes
                                    </legend>
                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Any additional information you want us to know
                                            about for this episode
                                        </span>
                                        <textarea
                                            name="notes"
                                            value={form.notes}
                                            onChange={handleChange}
                                            rows={3}
                                            className="form-field__textarea"
                                        />
                                    </label>
                                </fieldset>
                            )}

                            {/* Progress bar (only visible while modal is open) */}
                            {isUploading && (
                                <>
                                    <div className="progress">
                                        <div
                                            className="progress__bar"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <div
                                        className="text-description"
                                        style={{ marginTop: 6, marginBottom: 4 }}
                                    >
                                        Uploading… {mediaUploadPercent}%
                                    </div>
                                </>
                            )}

                            <div
                                className="modal__footer"
                                style={{
                                    justifyContent:
                                        !isFilesOnlyMode && isEditing
                                            ? "space-between"
                                            : "flex-end",
                                }}
                            >
                                {!isFilesOnlyMode && isEditing && (
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        {canWriteCurrentEpisode && (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="btn btn--danger"
                                            >
                                                Delete
                                            </button>
                                        )}
                                        {canViewChangeHistory && (
                                            <button
                                                type="button"
                                                onClick={openChangeLogModal}
                                                className="btn btn--secondary btn--small"
                                            >
                                                Change history
                                            </button>
                                        )}
                                        {isAdminUser &&
                                            canCreateSharePointFolder &&
                                            !String(form.sharepoint_folder_path || "").trim() && (
                                                <button
                                                    type="button"
                                                    onClick={handleCreateMissingSharePointFolder}
                                                    className="btn btn--secondary btn--small"
                                                    disabled={creatingSharePointFolder}
                                                >
                                                    {creatingSharePointFolder
                                                        ? "Creating…"
                                                        : "Create folder"}
                                                </button>
                                            )}
                                        <button
                                            type="button"
                                            onClick={openShareModal}
                                            className="btn btn--secondary"
                                        >
                                            Share
                                        </button>
                                        {isAdminUser &&
                                            String(form.audio_transcription || "").trim() && (
                                                <button
                                                    type="button"
                                                    onClick={openTranscriptPreviewModal}
                                                    className="btn btn--secondary"
                                                >
                                                    Episode Transcription
                                                </button>
                                            )}
                                    </div>
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    {isFilesOnlyMode ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={openEpisodeNotesQuickModal}
                                                className="btn btn--secondary"
                                            >
                                                Add comments
                                            </button>
                                            <button
                                                type="button"
                                                onClick={closeModal}
                                                className="btn btn--secondary"
                                            >
                                                Close
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={closeModal}
                                                className="btn btn--secondary"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={saveDisabled}
                                                className="btn btn--primary"
                                            >
                                                {saveLabel}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isMultitrackModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">
                                    Create multitrack production
                                </h3>
                                <p className="modal__subtitle">
                                    Choose at least two audio files from this
                                    folder and give each track a name.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeMultitrackModal}
                                className="modal__close-btn"
                                disabled={multitrackProcessing}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            {multitrackModalError && (
                                <div className="alert alert--error">
                                    {multitrackModalError}
                                </div>
                            )}

                            <label className="form-field">
                                <span className="form-field__label">
                                    Production name
                                </span>
                                <input
                                    type="text"
                                    className="form-field__input"
                                    value={multitrackProductionName}
                                    onChange={(e) =>
                                        setMultitrackProductionName(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Enter production name"
                                    disabled={multitrackProcessing}
                                />
                            </label>

                            {multitrackRows.map((row, index) => (
                                <div
                                    key={row.row_id}
                                    className="form-field"
                                    style={{
                                        marginTop: 12,
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 8,
                                        padding: 10,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 8,
                                        }}
                                    >
                                        <span className="form-field__label">
                                            Track {index + 1}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn--secondary btn--small"
                                            onClick={() =>
                                                handleRemoveMultitrackRow(
                                                    row.row_id
                                                )
                                            }
                                            disabled={
                                                multitrackRows.length <= 2 ||
                                                multitrackProcessing
                                            }
                                        >
                                            Remove
                                        </button>
                                    </div>

                                    <label className="form-field">
                                        <span className="form-field__label">
                                            File
                                        </span>
                                        <select
                                            className="form-field__select"
                                            value={row.item_id}
                                            onChange={(e) =>
                                                handleMultitrackRowFileChange(
                                                    row.row_id,
                                                    e.target.value
                                                )
                                            }
                                            disabled={multitrackProcessing}
                                        >
                                            <option value="">
                                                Select audio file
                                            </option>
                                            {currentFolderAudioFiles.map(
                                                (item) => (
                                                    <option
                                                        key={String(
                                                            item.id || ""
                                                        )}
                                                        value={String(
                                                            item.id || ""
                                                        )}
                                                    >
                                                        {String(
                                                            item.name || ""
                                                        )}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </label>

                                    <label
                                        className="form-field"
                                        style={{ marginTop: 8 }}
                                    >
                                        <span className="form-field__label">
                                            Track name
                                        </span>
                                        <input
                                            type="text"
                                            className="form-field__input"
                                            value={row.track_name}
                                            onChange={(e) =>
                                                handleMultitrackRowNameChange(
                                                    row.row_id,
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Required"
                                            disabled={multitrackProcessing}
                                        />
                                    </label>
                                </div>
                            ))}

                            <div style={{ marginTop: 12 }}>
                                <button
                                    type="button"
                                    className="btn btn--secondary btn--small"
                                    onClick={handleAddMultitrackRow}
                                    disabled={multitrackProcessing}
                                >
                                    + Add track
                                </button>
                            </div>

                            <label className="form-field" style={{ marginTop: 16 }}>
                                <span className="form-field__label">
                                    Auphonic preset
                                </span>
                                <select
                                    className="form-field__select"
                                    value={multitrackPresetUuid}
                                    onChange={(e) =>
                                        setMultitrackPresetUuid(
                                            String(e.target.value || "")
                                        )
                                    }
                                    disabled={
                                        multitrackProcessing ||
                                        multitrackPresetsLoading
                                    }
                                >
                                    <option value="">
                                        {multitrackPresetsLoading
                                            ? "Loading presets…"
                                            : multitrackPresets.length
                                              ? "Select preset"
                                              : "No presets loaded"}
                                    </option>
                                    {multitrackPresets.map((preset) => (
                                        <option
                                            key={String(preset.uuid || "")}
                                            value={String(preset.uuid || "")}
                                        >
                                            {String(preset.name || preset.uuid)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeMultitrackModal}
                                className="btn btn--secondary"
                                disabled={multitrackProcessing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmitMultitrackProduction}
                                className="btn btn--primary"
                                disabled={
                                    multitrackProcessing ||
                                    multitrackPresetsLoading
                                }
                            >
                                {multitrackProcessing
                                    ? "Submitting…"
                                    : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isProcessAudioPresetModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Process Audio</h3>
                                <p className="modal__subtitle">
                                    Choose the Auphonic preset for this file.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeProcessAudioPresetModal}
                                className="modal__close-btn"
                                disabled={processingAudio}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            {processAudioPresetError && (
                                <div className="alert alert--error">
                                    {processAudioPresetError}
                                </div>
                            )}
                            <div className="card-info-row">
                                <span className="card-info-row__label">File</span>
                                <span className="card-info-row__value">
                                    {String(processAudioPresetItem?.name || "")}
                                </span>
                            </div>
                            <label className="form-field" style={{ marginTop: 12 }}>
                                <span className="form-field__label">
                                    Auphonic preset
                                </span>
                                <select
                                    className="form-field__select"
                                    value={processAudioPresetUuid}
                                    onChange={(e) =>
                                        setProcessAudioPresetUuid(
                                            String(e.target.value || "")
                                        )
                                    }
                                    disabled={processingAudio || multitrackPresetsLoading}
                                >
                                    <option value="">
                                        {multitrackPresetsLoading
                                            ? "Loading presets…"
                                            : multitrackPresets.length
                                              ? "Select preset"
                                              : "No presets loaded"}
                                    </option>
                                    {multitrackPresets.map((preset) => (
                                        <option
                                            key={String(preset.uuid || "")}
                                            value={String(preset.uuid || "")}
                                        >
                                            {String(preset.name || preset.uuid)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeProcessAudioPresetModal}
                                className="btn btn--secondary"
                                disabled={processingAudio}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmitProcessAudioPreset}
                                className="btn btn--primary"
                                disabled={processingAudio || multitrackPresetsLoading}
                            >
                                {processingAudio ? "Processing…" : "Process Audio"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- SHARE EPISODE MODAL ---------- */}
            {isShareOpen && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Share Episode</h3>
                                <p className="modal__subtitle">
                                    Create a share link, then optionally send it by email.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeShareModal}
                                className="modal__close-btn"
                                disabled={shareBusy}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            {shareError ? (
                                <div className="alert alert--error">{shareError}</div>
                            ) : null}

                            {shareResult?.share_url ? (
                                <div className="card" style={{ marginTop: 8 }}>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">Share link</span>
                                        <span className="card-info-row__value">Ready to copy</span>
                                    </div>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">Expires</span>
                                        <span className="card-info-row__value">
                                            {String(shareResult.expires_at || "").slice(0, 10) ||
                                                "-"}
                                        </span>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <button
                                            type="button"
                                            className="btn btn--secondary btn--small"
                                            onClick={async () => {
                                                const text = String(shareResult.share_url || "");
                                                if (!text) return;
                                                try {
                                                    await navigator.clipboard.writeText(text);
                                                    addToast("Share link copied", "success", 2500);
                                                } catch {
                                                    window.prompt("Copy share link", text);
                                                }
                                            }}
                                        >
                                            Copy link
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {shareResult?.share_url ? (
                                <label className="form-field" style={{ marginTop: 12 }}>
                                    <span className="form-field__label">
                                        Email addresses (optional)
                                    </span>
                                    <textarea
                                        value={shareEmailsDraft}
                                        onChange={(e) => setShareEmailsDraft(e.target.value)}
                                        rows={4}
                                        className="form-field__textarea"
                                        placeholder="name@example.com; another@example.com"
                                        disabled={shareBusy}
                                    />
                                </label>
                            ) : null}
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeShareModal}
                                className="btn btn--secondary"
                                disabled={shareBusy}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={shareResult?.share_url ? handleSendShareEmails : handleCreateShare}
                                className="btn btn--primary"
                                disabled={
                                    shareBusy ||
                                    (shareResult?.share_url &&
                                        !String(shareEmailsDraft || "").trim())
                                }
                            >
                                {shareBusy
                                    ? shareResult?.share_url
                                        ? "Sending…"
                                        : "Creating…"
                                    : shareResult?.share_url
                                        ? "Send email"
                                        : "Create Share Link"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- REVIEW SEND NOTES MODAL ---------- */}
            {isReviewNotesPromptOpen && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Pending Approval</h3>
                                <p className="modal__subtitle">
                                    What changed for this version? (shown on review link)
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeReviewNotesPrompt}
                                className="modal__close-btn"
                                disabled={saving}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            <label className="form-field">
                                <span className="form-field__label">
                                    What changed for this version?
                                </span>
                                <textarea
                                    value={reviewNotesDraft}
                                    onChange={(e) => setReviewNotesDraft(e.target.value)}
                                    rows={4}
                                    className="form-field__textarea"
                                    placeholder="Optional: describe what changed before sending for approval."
                                    disabled={saving}
                                />
                            </label>
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeReviewNotesPrompt}
                                className="btn btn--secondary"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleReviewNotesPromptDone}
                                className="btn btn--primary"
                                disabled={saving}
                            >
                                {saving ? "Saving…" : "Done"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- QUICK NOTES MODAL (FROM FILES POPUP) ---------- */}
            {isEpisodeNotesQuickOpen && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Episode Notes</h3>
                                <p className="modal__subtitle">
                                    Any additional information you want us to know
                                    about for this episode.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeEpisodeNotesQuickModal}
                                className="modal__close-btn"
                                disabled={episodeNotesQuickSaving}
                            >
                                ✕
                            </button>
                        </div>

                        {episodeNotesQuickError && (
                            <div className="alert alert--error">
                                {episodeNotesQuickError}
                            </div>
                        )}

                        <div className="modal__body">
                            <label className="form-field">
                                <textarea
                                    value={episodeNotesDraft}
                                    onChange={(e) =>
                                        setEpisodeNotesDraft(e.target.value)
                                    }
                                    rows={5}
                                    className="form-field__textarea"
                                    disabled={!canEditEpisodeNotes || episodeNotesQuickSaving}
                                />
                            </label>
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeEpisodeNotesQuickModal}
                                className="btn btn--secondary"
                                disabled={episodeNotesQuickSaving}
                            >
                                {canEditEpisodeNotes ? "Cancel" : "Close"}
                            </button>
                            {canEditEpisodeNotes && (
                                <button
                                    type="button"
                                    onClick={saveEpisodeNotesQuickModal}
                                    className="btn btn--primary"
                                    disabled={episodeNotesQuickSaving}
                                >
                                    {episodeNotesQuickSaving ? "Saving…" : "Save"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isTranscriptPreviewOpen && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Episode Transcription</h3>
                                <p className="modal__subtitle">
                                    Read-only transcript saved on this episode.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeTranscriptPreviewModal}
                                className="modal__close-btn"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            <label className="form-field">
                                <span className="form-field__label">Transcript</span>
                                <textarea
                                    value={form.audio_transcription || ""}
                                    readOnly
                                    rows={14}
                                    className="form-field__textarea"
                                />
                            </label>
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            {isAdminUser &&
                                String(form.audio_transcription || "").trim() && (
                                    <button
                                        type="button"
                                        onClick={handleGenerateShowNotesFromTranscript}
                                        className="btn btn--secondary"
                                        disabled={generatingShowNotes}
                                    >
                                        {generatingShowNotes
                                            ? "Generating Show Notes…"
                                            : "Generate Show Notes"}
                                    </button>
                                )}
                            <button
                                type="button"
                                onClick={closeTranscriptPreviewModal}
                                className="btn btn--secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- NOTIFICATION MODAL ---------- */}
            {isNotifyOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Send Notification</h3>
                                <p className="modal__subtitle">
                                    This will notify the podcast owners for:{" "}
                                    <strong>
                                        {form.title || "Untitled episode"}
                                    </strong>
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeNotifyModal}
                                className="modal__close-btn"
                            >
                                ✕
                            </button>
                        </div>

                        {notifyError && (
                            <div className="alert alert--error">
                                {notifyError}
                            </div>
                        )}
                        {notifySuccess && (
                            <div
                                className="alert"
                                style={{
                                    background: "#ecfdf3",
                                    border: "1px solid #bbf7d0",
                                    color: "#166534",
                                }}
                            >
                                {notifySuccess}
                            </div>
                        )}

                        <form
                            onSubmit={handleSendNotification}
                            className="modal__body"
                            ref={notifyFormRef}
                        >
                            <fieldset className="fieldset">
                                <legend className="fieldset__legend">
                                    Message
                                </legend>

                                <label className="form-field">
                                    <span className="form-field__label">
                                        Message
                                    </span>
                                    <textarea
                                        value={notifyMessage}
                                        onChange={(e) =>
                                            setNotifyMessage(e.target.value)
                                        }
                                        rows={5}
                                        className="form-field__textarea"
                                        placeholder="Write the message you want to send…"
                                    />
                                </label>
                            </fieldset>

                            <div
                                className="modal__footer"
                                style={{ justifyContent: "flex-end" }}
                            >
                                <button
                                    type="button"
                                    onClick={closeNotifyModal}
                                    className="btn btn--secondary"
                                    disabled={notifying}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn--primary"
                                    disabled={notifying}
                                >
                                    {notifying ? "Sending…" : "Send"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ---------- CHANGE HISTORY MODAL ---------- */}
            {isChangeLogOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Change History</h3>
                                <p className="modal__subtitle">
                                    Audit trail for:{" "}
                                    <strong>
                                        {form.title || "Untitled episode"}
                                    </strong>
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeChangeLogModal}
                                className="modal__close-btn"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            {changeLogsLoading && <p>Loading change history…</p>}

                            {changeLogsError && (
                                <div className="alert alert--error">
                                    {changeLogsError}
                                </div>
                            )}

                            {!changeLogsLoading &&
                                !changeLogsError &&
                                sortedChangeLogs.length === 0 && (
                                    <p>
                                        No changes recorded for this episode yet.
                                    </p>
                                )}

                            {!changeLogsLoading &&
                                !changeLogsError &&
                                sortedChangeLogs.length > 0 && (
                                    <table className="change-log-table">
                                        <colgroup>
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "40%" }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>User</th>
                                                <th>Source</th>
                                                <th>Action</th>
                                                <th>What changed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedChangeLogs.map((log) => (
                                                <tr key={log.id}>
                                                    <td>
                                                        {log.created_at
                                                            ? formatDateTime(
                                                                log.created_at
                                                            )
                                                            : "—"}
                                                    </td>
                                                    <td>
                                                        {log.changed_by_name ||
                                                            "—"}
                                                    </td>
                                                    <td>
                                                        {log.source || "—"}
                                                    </td>
                                                    <td>
                                                        {log.action || "—"}
                                                    </td>
                                                    <td>{summariseLog(log)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeChangeLogModal}
                                className="btn btn--secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- SUGGESTED CHANGES MODAL ---------- */}
            {isSuggestModalOpen && suggestedChanges && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Review Suggested Changes</h3>
                                <p className="modal__subtitle">
                                    Compare the client’s suggestions to the current episode
                                    title and show notes.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeSuggestModal}
                                className="modal__close-btn"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            {/* Title comparison */}
                            {typeof suggestedChanges.title === "string" &&
                                suggestedChanges.title.trim() !== "" && (
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">Title</legend>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 12,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {/* Current title with diff */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    className="form-field__label"
                                                    style={{ marginBottom: 4 }}
                                                >
                                                    Current title
                                                </div>
                                                <div className="portal-compare-box">
                                                    <div className="change-log-diff__value">
                                                        {renderTextDiff(
                                                            form.title || "",
                                                            suggestedChanges.title || "",
                                                            "old"
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Suggested title with diff */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    className="form-field__label"
                                                    style={{ marginBottom: 4 }}
                                                >
                                                    Suggested title
                                                </div>
                                                <div className="portal-compare-box">
                                                    <div className="change-log-diff__value">
                                                        {renderTextDiff(
                                                            form.title || "",
                                                            suggestedChanges.title || "",
                                                            "new"
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <label
                                            className="form-field__checkbox-row"
                                            style={{ marginTop: 8 }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={applyTitleSuggestion}
                                                onChange={(e) =>
                                                    setApplyTitleSuggestion(e.target.checked)
                                                }
                                            />
                                            <span className="form-field__label">
                                                Apply suggested title
                                            </span>
                                        </label>
                                    </fieldset>
                                )}

                            {/* Show notes comparison */}
                            {typeof suggestedChanges.show_notes_captivate === "string" &&
                                suggestedChanges.show_notes_captivate.trim() !== "" && (
                                    <fieldset
                                        className="fieldset"
                                        style={{ marginTop: 12 }} // gap between Title and Show Notes
                                    >
                                        <legend className="fieldset__legend">
                                            Show Notes
                                        </legend>

                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 12,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {/* Current show notes – HTML with diff */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    className="form-field__label"
                                                    style={{ marginBottom: 4 }}
                                                >
                                                    Current show notes
                                                </div>
                                                <div
                                                    className="portal-compare-box portal-compare-box--scroll portal-quill-editor"
                                                    ref={currentNotesScrollRef}
                                                    onScroll={() =>
                                                        handleShowNotesScroll("current")
                                                    }
                                                >
                                                    <div
                                                        className="ql-editor"
                                                        dangerouslySetInnerHTML={{
                                                            __html: sanitizeHtml(
                                                                buildHtmlDiff(
                                                                    form.show_notes_captivate ||
                                                                    "",
                                                                    suggestedChanges.show_notes_captivate ||
                                                                    "",
                                                                    "old"
                                                                ) ||
                                                                "<p><em>No show notes set.</em></p>"
                                                            ),
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Suggested show notes – HTML with diff */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    className="form-field__label"
                                                    style={{ marginBottom: 4 }}
                                                >
                                                    Suggested show notes
                                                </div>
                                                <div
                                                    className="portal-compare-box portal-compare-box--scroll portal-quill-editor"
                                                    ref={suggestedNotesScrollRef}
                                                    onScroll={() =>
                                                        handleShowNotesScroll("suggested")
                                                    }
                                                >
                                                    <div
                                                        className="ql-editor"
                                                        dangerouslySetInnerHTML={{
                                                            __html: sanitizeHtml(
                                                                buildHtmlDiff(
                                                                    form.show_notes_captivate ||
                                                                    "",
                                                                    suggestedChanges.show_notes_captivate ||
                                                                    "",
                                                                    "new"
                                                                ) ||
                                                                "<p><em>No suggested show notes.</em></p>"
                                                            ),
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <label
                                            className="form-field__checkbox-row"
                                            style={{ marginTop: 8 }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={applyShowNotesSuggestion}
                                                onChange={(e) =>
                                                    setApplyShowNotesSuggestion(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <span className="form-field__label">
                                                Apply suggested show notes
                                            </span>
                                        </label>
                                    </fieldset>
                                )}

                            {!(
                                (suggestedChanges.title &&
                                    suggestedChanges.title.trim() !== "") ||
                                (suggestedChanges.show_notes_captivate &&
                                    suggestedChanges.show_notes_captivate.trim() !== "")
                            ) && (
                                    <p>
                                        There are no usable suggested changes for this episode.
                                    </p>
                                )}
                        </div>

                        <div
                            className="modal__footer"
                            style={{
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn--secondary btn--danger-outline"
                                onClick={handleDiscardSuggestedChanges}
                            >
                                Discard all suggested changes
                            </button>

                            <div
                                style={{
                                    display: "flex",
                                    gap: 8,
                                    justifyContent: "flex-end",
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={closeSuggestModal}
                                    className="btn btn--secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAcceptSuggestedChanges}
                                    className="btn btn--primary"
                                >
                                    Apply selected changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {episodeFilePreviewOpen && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: 560 }}>
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Preview</h3>
                                <p className="modal__subtitle">
                                    {episodeFilePreviewName || "File preview"}
                                </p>
                            </div>
                            <button
                                className="modal__close-btn"
                                onClick={closeEpisodeFilePreview}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal__body">
                            {episodeFilePreviewLoading && <p>Loading preview...</p>}
                            {!episodeFilePreviewLoading && episodeFilePreviewError && (
                                <div className="alert alert--error">
                                    {episodeFilePreviewError}
                                </div>
                            )}
                            {!episodeFilePreviewLoading &&
                                !episodeFilePreviewError &&
                                episodeFilePreviewUrl &&
                                episodeFilePreviewType === "audio" && (
                                    <AudioPlayer src={episodeFilePreviewUrl} />
                                )}
                            {!episodeFilePreviewLoading &&
                                !episodeFilePreviewError &&
                                episodeFilePreviewUrl &&
                                episodeFilePreviewType === "video" && (
                                    <video
                                        src={episodeFilePreviewUrl}
                                        controls
                                        preload="metadata"
                                        style={{
                                            width: "100%",
                                            maxHeight: "70vh",
                                            borderRadius: 8,
                                            background: "#000",
                                        }}
                                    />
                                )}
                            {!episodeFilePreviewLoading &&
                                !episodeFilePreviewError &&
                                episodeFilePreviewUrl &&
                                episodeFilePreviewType === "image" && (
                                    <img
                                        src={episodeFilePreviewUrl}
                                        alt={episodeFilePreviewName || "Image preview"}
                                        style={{
                                            width: "100%",
                                            maxHeight: "70vh",
                                            objectFit: "contain",
                                            borderRadius: 8,
                                        }}
                                    />
                                )}
                        </div>
                        <div className="modal__footer">
                            <span />
                            <button
                                type="button"
                                className="btn btn--secondary"
                                onClick={closeEpisodeFilePreview}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- TOASTS (GLOBAL) ---------- */}
            {toasts.length > 0 && (
                <div className="toast-container">
                    {toasts.map((toast) => (
                        <div
                            key={toast.id}
                            className={`toast toast--${toast.type}`}
                        >
                            <span className="toast__message">
                                {toast.message}
                            </span>
                            <button
                                type="button"
                                className="toast__close"
                                onClick={() => removeToast(toast.id)}
                                aria-label="Dismiss notification"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </PageShell>
    );
}

export default Episodes;
